import { io } from "socket.io-client";
import React, { useState, useRef, useEffect, useReducer } from "react";
import { observerReducer, isObserverVisible, initialObserverState } from './observerPresence';
import BookLoaderScreen from './BookLoaderScreen';
import * as mediasoupClient from "mediasoup-client";
import confetti from "canvas-confetti";
// CORS-Fix Version 2.0 - 24.08.2025
import './lrstyle.css';
import './lrThemes.css';
import { BrowserRouter, Routes, Route, useParams } from 'react-router-dom';
import ModView from './ModView';
import Whiteboard from './Whiteboard';
import VideoView from './VideoView';
import DeviceBlocker from './DeviceDetection';
import { DndContext, useDraggable, useDroppable } from "@dnd-kit/core";
import { allPlaced, allCorrect, buildPlacementDetails } from "./multiDragdropOps";
import { shuffleArray, mulberry32 } from './questionShuffle';
import { getIceServers } from './iceConfig';
import { withRetry, emitAsync, isTransientCameraError, shouldRecoverTrack } from './streamUtils';
import CalculatorWindow from './CalculatorWindow';
import BasicCalculator from './BasicCalculator';
import GeoGebraCalculator from './GeoGebraCalculator';


function LearningRoom() {
  const { roomId } = useParams();
  
  // ✅ Token-Authentifizierung
  const [authValidated, setAuthValidated] = useState(false);
  // Ladeanimation-Aufbau mindestens einmal komplett gezeigt (siehe BookLoaderScreen)
  const [loaderMinShown, setLoaderMinShown] = useState(false);
  const [authUser, setAuthUser] = useState(null);
  const [authToken, setAuthToken] = useState(null);
  const [deviceCheckComplete, setDeviceCheckComplete] = useState(false);
  const [clientRole, setClientRole] = useState('student'); // 'student' | 'observer'
  const [observerAudioUnlocked, setObserverAudioUnlocked] = useState(false); // Observer: Autoplay-Sperre per Nutzer-Geste aufheben
  const [observerState, dispatchObserver] = useReducer(observerReducer, initialObserverState);

  const modVideoRef = useRef(null);
  const modPreviewRef = useRef(null);
  // Prevent multiple modStream consumption - useRef vermeidet stale-closure-Problem in Event-Handlern
  const modStreamConsumedRef = useRef(false);
  const [modStreamConsumed, setModStreamConsumed] = useState(false); // kept for compatibility
  // Fragenliste: Der Moderator ist die ALLEINIGE Quelle für Reihenfolge, Umfang und
  // Options-Anordnung. Sobald der Moderator eine Liste sendet (questionsListUpdated),
  // zeigt der Schüler exakt diese – dadurch sieht der Schüler bei jeder Frage dieselben
  // Antwortmöglichkeiten wie der Moderator (nur die Anordnung ist bewusst gemischt).
  // Die selbst geladene Liste (ownQuestions) dient nur noch als Fallback, solange KEINE
  // Moderator-Liste vorliegt (z. B. Video-Session ohne Quiz oder ein Alt-Moderator-Client).
  const [moderatorQuestions, setModeratorQuestions] = useState([]);
  const [moderatorProvided, setModeratorProvided] = useState(false);
  const [ownQuestions, setOwnQuestions] = useState([]);
  const questions = moderatorProvided ? moderatorQuestions : ownQuestions;
  const [loading, setLoading] = useState(true);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  // Indizes der Fragen, die der Schüler tatsächlich beantwortet hat.
  // Unbeantwortete Fragen zählen so nicht als "falsch" in der Ergebnisübersicht.
  const [answeredQuestions, setAnsweredQuestions] = useState(() => new Set());
  const [showNextButton, setShowNextButton] = useState(false);
  const [quizFinished, setQuizFinished] = useState(false);
  const [placedItems, setPlacedItems] = useState([]);
  const [multiPlacements, setMultiPlacements] = useState({}); // { [targetId]: pieceId } für multiDragdrop
  const [gapInputs, setGapInputs] = useState({});
  const [puzzleOrder, setPuzzleOrder] = useState([]);
  const [mcSelected, setMcSelected] = useState([]);
  const [cwInputs, setCwInputs] = useState({});
  const studentSelfVideoRef = useRef(null);
  const quizBoxRef = useRef(null);
  // --- Socket.IO Client: moved to top-level ---
  const socket = useRef(null);
  const [isQuizStarted, setIsQuizStarted] = useState(false);

  const prevQuestionsLengthRef = useRef(0);

  // === NEW: View Management ===
  const [currentView, setCurrentView] = useState('waiting'); // 'waiting', 'video', 'quiz', 'success'
  
  // === NEW: Session Type Management ===
  const [isVideoSession, setIsVideoSession] = useState(false); // Unterscheidet Video-Sessions von Quiz-Sessions

  // === NEW: Multi-User Navigation Control ===
  const [totalStudentsInRoom, setTotalStudentsInRoom] = useState(1);
  const [canStudentNavigate, setCanStudentNavigate] = useState(true);
  const [calculatorMode, setCalculatorMode] = useState('off'); // 'off' | 'basic' | 'full'

  // === NEW: Student Identity ===
  const [studentNumber, setStudentNumber] = useState(null);
  const [studentId, setStudentId] = useState(null);

  // Confetti function (moved up for early usage)
  const launchConfetti = () => {
    confetti({
      particleCount: 200,
      spread: 70,
      origin: { y: 0.6 },
    });
  };

  // --- Mediasoup Refs ---
  const deviceRef = useRef(null);
  const sendTransportRef = useRef(null);
  const peerAudioRefs = useRef([]); // Schüler-zu-Schüler Audio-Elemente für Cleanup
  const [checkStates, setCheckStates] = useState({
    connected: false,
    system: false,
    teacher: false
  });
  const [modDisplayName, setModDisplayName] = useState(null);
  // Klassenstufe der Buchung (aus dem Token-Validator). Bestimmt das Schüler-Design.
  const [klassenstufe, setKlassenstufe] = useState(null);
  // --- MOD STREAM STATES ---
  const [modStreamAvailable, setModStreamAvailable] = useState(false);
  const [modVideoMuted, setModVideoMuted] = useState(false); // Lehrer hat Kamera gemutet → Kurt zeigen
  const [modStreamActivated, setModStreamActivated] = useState(false);
  const [modAudioAvailable, setModAudioAvailable] = useState(false);
  // Audio-Join State
  const [audioJoined, setAudioJoined] = useState(false);

  // Move handleNext above its first use and stabilize with useCallback
  const handleNext = React.useCallback((fromModerator = false) => {
    // Navigation-Kontrolle: Schüler kann nur navigieren wenn er der einzige ist
    // ABER: Moderator kann IMMER navigieren
    if (!fromModerator && !canStudentNavigate) {
      console.log("🚫 Navigation von Schüler blockiert - warte auf Dozent");
      return;
    }

    if (currentQuestionIndex + 1 < questions.length) {
      const nextIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(nextIndex);
      socket.current?.emit("questionChanged", {
        roomId: roomId || "defaultRoom",
        questionIndex: nextIndex
      });
      setSelectedAnswer(null);
      setShowNextButton(false);
      setPlacedItems([]);
      setMultiPlacements({});
      setGapInputs({});
      setPuzzleOrder([]);
      setMcSelected([]);
      setCwInputs({});
    } else {
      // === NEW: Don't automatically finish quiz, let moderator control this ===
      console.log("🏁 Quiz ende erreicht - warte auf Moderator-Entscheidung");
      // Emit event to notify moderator that quiz could be finished
      socket.current?.emit("quizCanFinish", { roomId: roomId || "defaultRoom" });
    }
  }, [currentQuestionIndex, questions.length, roomId, canStudentNavigate]);

  // Callback-Funktion für DeviceBlocker
  const handleDeviceCheckComplete = React.useCallback(() => {
    console.log('🔄 [DEBUG] DeviceBlocker Callback ausgeführt');
    setDeviceCheckComplete(true);
  }, []);

  // ✅ Token-Authentifizierung (erster useEffect)
  useEffect(() => {
    console.log('🔐 [DEBUG] Starte Token-Validierung sofort');
    
    // Lese Token aus der URL
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const roomLink = window.location.pathname.split('/room/')[1] || roomId;
    
    if (!token) {
      alert('🔒 Kein Authentifizierungs-Token vorhanden. Zugriff verweigert.');
      window.location.href = "/";
      return;
    }

    setAuthToken(token);

    // Rolle aus JWT-Payload lesen (observer vs. student)
    let detectedRole = 'student';
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.role === 'observer') detectedRole = 'observer';
    } catch (e) { /* Fallback: student */ }
    setClientRole(detectedRole);

    if (!roomLink) {
      alert('🔒 Room-Link nicht gefunden.');
      window.location.href = "/";
      return;
    }
    
    // Token via OKADIA Token-Validator validieren
    fetch('https://okadia.de/lr_token_validator.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        token: token,
        room_link: roomLink,
        role: detectedRole
      })
    })
    .then(res => res.json())
    .then(data => {
      if (data.success) {
        console.log('✅ Schüler-Token erfolgreich validiert:', data.user);
        // data.user_name enthält den echten Namen aus dem Account (data.user.name ist nur ein generischer Platzhalter)
        const resolvedUser = {
          ...data.user,
          name: data.user_name || data.user?.name || null,
          sn: data.user_sn || data.user?.sn || null,
        };
        console.log('👤 Resolved authUser:', resolvedUser);
        setAuthUser(resolvedUser);

        // Schüler-SN setzen falls vorhanden
        if (resolvedUser.sn) {
          setStudentId(resolvedUser.sn);
          console.log('👤 Schüler-SN gesetzt:', resolvedUser.sn);
        }

        // Klassenstufe aus der Buchung übernehmen (additiv – fehlt bei alten Backends).
        const ks = data.klassenstufe ?? data.user?.klassenstufe ?? null;
        if (ks !== null && ks !== undefined && String(ks).trim() !== '') {
          setKlassenstufe(String(ks).trim());
          console.log('🎓 Klassenstufe gesetzt:', ks);
        }

        setAuthValidated(true);
      } else {
        alert(`🔒 Token-Validierung fehlgeschlagen: ${data.message || 'Unbekannter Fehler'}`);
        window.location.href = "/";
      }
    })
    .catch((error) => {
      console.error('❌ Token-Validierungsfehler:', error);
      alert('🔒 Fehler bei der Token-Validierung.');
      window.location.href = "/";
    });
  }, [roomId]);

  useEffect(() => {
    if (!authValidated || !authToken) return;

    // Socket.IO wird für alle Sessions benötigt (ViewSlider-Kommunikation)
    console.log(`🔐 Verbinde Socket.IO für Session: ${roomId} (VideoSession: ${isVideoSession})`);
    
    // Resilient socket connect: try primary URL, then origin
    const socketCandidates = [];
    if (process.env.REACT_APP_SOCKET_URL) socketCandidates.push(process.env.REACT_APP_SOCKET_URL);
    // Use Socket Server (Port 3002) for Room Management
    socketCandidates.push('https://rooms.okadia.de');

    let connected = false;
    let currentSocket = null;

    const tryConnect = (index) => {
      if (index >= socketCandidates.length) return; // nothing left
      const url = socketCandidates[index];
      const actualRoomId = roomId || "defaultRoom";
      try {
        currentSocket = io(url, {
          transports: ['polling', 'websocket'], // Start with polling, upgrade to websocket
          timeout: 8000,
          reconnection: false, // we'll manage reconnect logic manually
          forceNew: true, // Force new connection each time
          auth: {
            token: authToken,
            roomId: actualRoomId,
            role: clientRole
          }
        });

        const onConnect = () => {
          connected = true;
          socket.current = currentSocket;
          socket.current.emit("joinRoom", {
            role: clientRole,
            roomId: actualRoomId
          });
          // wire common events
          socket.current.on("quizStarted", () => { 
            console.log("🎯 QuizStarted-Event empfangen!"); 
            setIsQuizStarted(true); 
            setCurrentView('quiz'); // NEW: Set view to quiz
            if (questions.length > 0) { 
              socket.current.emit("questionChanged", { roomId: roomId || "defaultRoom", questionIndex: 0 }); 
            } 
          });

          // === NEW: View Management Events ===
          socket.current.on("viewChanged", (data) => {
            console.log("📺 [STUDENT] View changed to:", data.view);
            console.log("📺 [STUDENT] Current socket ID:", socket.current.id);
            console.log("📺 [STUDENT] Room ID:", actualRoomId);
            setCurrentView(data.view);
            
            // Handle specific view changes
            if (data.view === 'quiz') {
              setIsQuizStarted(true);
            } else if (data.view === 'waiting') {
              setIsQuizStarted(false);
              setQuizFinished(false);
            } else if (data.view === 'success') {
              setQuizFinished(true);
              launchConfetti();
            }
          });

          // === MODERATOR VIEW MANAGEMENT ===
          socket.current.on("moderatorSetView", (data) => {
            console.log("📺 Moderator set view to:", data.view);
            setCurrentView(data.view);
            
            // Handle specific view changes
            if (data.view === 'quiz') {
              setIsQuizStarted(true);
            } else if (data.view === 'waiting') {
              setIsQuizStarted(false);
              setQuizFinished(false);
            } else if (data.view === 'success') {
              setQuizFinished(true);
              launchConfetti();
            }
          });

          // === NAVIGATION CONTROL FROM MODERATOR ===
          socket.current.on("navigationControlUpdate", (data) => {
            console.log('🎯 [STUDENT] Navigation control event received:', data);
            console.log('🎯 [STUDENT] allowNavigation:', data.allowNavigation);
            setCanStudentNavigate(data.allowNavigation);
            console.log(`✅ [STUDENT] canStudentNavigate gesetzt auf: ${data.allowNavigation}`);
          });

          // === AUTORITATIVE, ABSOLUTE FRAGENSYNCHRONISATION (Lehrer-gesteuert) ===
          // Server sendet den exakten Zielindex; Client übernimmt ihn direkt (idempotent,
          // kein Echo). MUSS hier in onConnect registriert werden, damit der Listener
          // garantiert an einem bereits verbundenen Socket hängt.
          socket.current.on("syncQuestion", (data) => {
            const idx = parseInt(data?.questionIndex, 10);
            if (isNaN(idx)) return;
            console.log('🧭 [STUDENT] syncQuestion -> Index', idx);
            setCurrentQuestionIndex(Math.max(0, idx));
            // Antwort-/Eingabe-State für die neue Frage zurücksetzen
            setSelectedAnswer(null);
            setShowNextButton(false);
            setPlacedItems([]);
            setMultiPlacements({});
            setGapInputs({});
            setPuzzleOrder([]);
            setMcSelected([]);
            setCwInputs({});
          });

          // === CALCULATOR RELEASE FROM MODERATOR ===
          socket.current.on("calculatorModeUpdate", (data) => {
            const mode = ['off', 'basic', 'full'].includes(data?.mode) ? data.mode : 'off';
            console.log('🧮 [STUDENT] calculatorMode:', mode);
            setCalculatorMode(mode);
          });

          // === MODERATOR AUDIO/VIDEO MUTE-STATUS ===
          // Bei gemutetem Lehrervideo Kurt statt schwarzem Bild einblenden.
          socket.current.on("modMediaState", (data) => {
            console.log('🔇 [STUDENT] modMediaState:', data);
            setModVideoMuted(!!data?.videoMuted);
          });

          // === MODERATOR INFO (display name) ===
          socket.current.on("moderatorInfo", (data) => {
            if (data.name) {
              console.log("👨‍🏫 Moderator-Name empfangen:", data.name);
              setModDisplayName(data.name);
            }
          });

          // === QUESTIONS LIST UPDATE FROM MODERATOR ===
          socket.current.on("questionsListUpdated", (data) => {
            console.log('📚 [STUDENT] Questions list updated from moderator:', data);
            if (data.questions && Array.isArray(data.questions)) {
              // Moderator-Liste ist ab jetzt maßgeblich und überschreibt jede selbst
              // geladene Liste – so bleiben Schüler und Moderator index-gleich.
              setModeratorQuestions(data.questions);
              setModeratorProvided(true);
              console.log(`✅ [STUDENT] ${data.questions.length} Fragen aktualisiert`);
              
              // Wenn aktuelle Frage jetzt außerhalb des gültigen Bereichs ist
              if (currentQuestionIndex >= data.questions.length) {
                const newIndex = Math.max(0, data.questions.length - 1);
                setCurrentQuestionIndex(newIndex);
                console.log(`⚠️ [STUDENT] Frage-Index angepasst auf ${newIndex}`);
              }
            }
          });
          
          // Multi-User Events
          socket.current.on("roomFull", (data) => { 
            console.log("🚫 Raum ist voll:", data);
            alert(`${data.message}\nAktuelle Kapazität: ${data.currentCapacity}/${data.maxCapacity}\n\nSie werden zur Startseite weitergeleitet.`);
            
            // Verhindere weitere Verbindungsversuche
            if (currentSocket) {
              currentSocket.off(); // Entferne alle Event-Listener
              currentSocket.close(); // Schließe die Verbindung
            }
            
            // Leite zur Startseite weiter
            setTimeout(() => {
              window.location.href = "/";
            }, 2000);
          });
          
          socket.current.on("joinSuccess", (data) => {
            console.log("✅ Erfolgreich beigetreten:", data);
            console.log(`🏷️ Sie sind: ${data.studentInfo.name}`);
            console.log(`📊 Raum-Kapazität: ${data.roomCapacity}`);
            
            // WICHTIG: Speichere Student-Identifikation für Antworten
            if (data.studentInfo) {
              setStudentNumber(data.studentInfo.studentNumber);
              setStudentId(data.studentInfo.id || data.studentInfo.socketId);
              console.log(`🆔 Student-ID gesetzt: ${data.studentInfo.id || data.studentInfo.socketId}, Student-Nummer: ${data.studentInfo.studentNumber}`);
            }
          });
          
          socket.current.on("roomUpdate", (data) => {
            console.log("🔄 Room-Update erhalten:", data);
            console.log(`👥 Aktuelle Schüler: ${data.totalStudents}/4`);
            data.students.forEach(s => console.log(`  - ${s.name} (${s.id})`));
          });
          
          socket.current.on("moderatorJoined", (data) => {
            console.log("🎓 Moderator beigetreten:", data.message);
          });
          
          socket.current.on("moderatorLeft", (data) => {
            console.log("👋 Moderator verlassen:", data.message);
          });
          
          // Behandle forcierte Disconnects
          socket.current.on("disconnect", (reason) => {
            console.log("🔌 Socket disconnected:", reason);
            if (reason === "io server disconnect") {
              // Server hat die Verbindung getrennt (z.B. wegen vollem Raum)
              console.log("⚠️ Server hat Verbindung getrennt - wahrscheinlich Raum voll");
            }
          });
          
          socket.current.on("connect_error", (error) => {
            console.error("❌ Socket connection error:", error);
          });
        };

        const onError = (err) => {
          // try next candidate
          currentSocket.off();
          currentSocket.close?.();
          tryConnect(index + 1);
        };

        currentSocket.once('connect', onConnect);
        currentSocket.once('connect_error', onError);
        currentSocket.once('error', onError);
      } catch (e) {
        tryConnect(index + 1);
      }
    };

    tryConnect(0);

    return () => {
      if (currentSocket) currentSocket.disconnect();
    };
  }, [roomId, authValidated, authToken, clientRole]);

  // (Der syncQuestion-Listener wird in onConnect registriert – siehe oben.)

  // Send student name after authentication is complete
  useEffect(() => {
    console.log(`🔍 [DEBUG] updateStudentName useEffect triggered: authValidated=${authValidated}, authUser=${JSON.stringify(authUser)}`);
    
    if (!authValidated || !authUser?.name) {
      console.log(`🔍 [DEBUG] Skipping updateStudentName: authValidated=${authValidated}, authUser.name=${authUser?.name}`);
      return;
    }
    
    console.log(`👤 Sende Schüler-Namen "${authUser.name}" an konsolidierte Socket nach Authentifizierung`);
    
    // Update Socket.IO with student name (now serves both quiz and video)
    if (socket.current) {
      console.log(`🔍 [DEBUG] Sending updateStudentName to socket: ${socket.current.id}`);
      socket.current.emit("updateStudentName", { 
        studentName: authUser.name 
      });
    } else {
      console.log(`🔍 [DEBUG] socket.current is null - cannot send updateStudentName`);
    }
  }, [authValidated, authUser?.name, roomId]);

  // NEW: Listen for room updates to control navigation
  useEffect(() => {
    if (!socket.current) return;

    const roomUpdateHandler = (data) => {
      console.log('🔄 Room update received in student app:', data);
      setTotalStudentsInRoom(data.totalStudents || 1);
      
      // Deaktiviere Navigation wenn mehr als 1 Schüler im Raum (KORRIGIERT)
      const shouldAllowNavigation = (data.totalStudents || 1) <= 1;
      setCanStudentNavigate(shouldAllowNavigation);
      
      if (!shouldAllowNavigation && showNextButton) {
        console.log("🚫 Verstecke Next Button - Multi-User Modus aktiviert");
        setShowNextButton(false);
      }
    };

    socket.current.on("roomUpdate", roomUpdateHandler);
    
    return () => {
      socket.current.off("roomUpdate", roomUpdateHandler);
    };
  }, [showNextButton]);

  // Get the current question after state and refs are initialized.
  // Index defensiv gegen die obere Grenze clampen, falls ein Sync-Index kurzzeitig
  // über die (noch nicht vollständig geladene) Fragenliste hinausgeht.
  const currentQuestion = questions[Math.min(currentQuestionIndex, Math.max(questions.length - 1, 0))];
  const progressPercent = Math.round(((currentQuestionIndex + 1) / questions.length) * 100);


  useEffect(() => {
    const delay = (key, min, max) => {
      const time = Math.floor(Math.random() * (max - min + 1)) + min;
      setTimeout(() => {
        setCheckStates(prev => ({ ...prev, [key]: true }));
      }, time);
    };

    delay("connected", 2000, 5000);
    delay("system", 5000, 10000);
  }, []);

  useEffect(() => {
    if (isQuizStarted) {
      setTimeout(() => {
        setCheckStates(prev => ({ ...prev, teacher: true }));
      }, 800);
      // questionChanged beim Quiz-Start emittieren (entkoppelt von checkAndLoadQuestions)
      if (socket.current && questions.length > 0) {
        socket.current.emit("questionChanged", {
          roomId: roomId || "defaultRoom",
          questionIndex: 0
        });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isQuizStarted]);


  useEffect(() => {
    if (socket.current && selectedAnswer !== null) {
      // Diese Frage wurde beantwortet (idempotent, auch bei erneutem Ansehen).
      setAnsweredQuestions(prev => {
        if (prev.has(currentQuestionIndex)) return prev;
        const next = new Set(prev);
        next.add(currentQuestionIndex);
        return next;
      });

      console.log("🔥 Sending answerUpdate:", {
        roomId: roomId || "defaultRoom",
        questionIndex: currentQuestionIndex,
        correct: selectedAnswer === currentQuestion?.correct || selectedAnswer === "correct",
      });
      
      socket.current.emit("answerUpdate", {
        roomId: roomId || "defaultRoom",
        questionIndex: currentQuestionIndex,
        correct: selectedAnswer === currentQuestion?.correct || selectedAnswer === "correct",
      });
      
      // Sende die spezifische Antwort an den Moderator
      const studentAnswerData = {
        roomId: roomId || "defaultRoom", // KORRIGIERT: Verwende echte roomId statt hardcoded "test123"
        questionIndex: currentQuestionIndex,
        answer: selectedAnswer,
        questionType: currentQuestion?.type,
        correct: selectedAnswer === currentQuestion?.correct || selectedAnswer === "correct",
        timestamp: new Date().toISOString(),
        studentNumber: studentNumber || Math.floor(Math.random() * 4) + 1, // Fallback: Zufällige Nummer 1-4
        studentId: studentId || socket.current.id, // Fallback: Socket-ID verwenden
        ...(currentQuestion?.type === "multiDragdrop"
          ? { placements: buildPlacementDetails(multiPlacements, currentQuestion.pieces || [], currentQuestion.targets || []) }
          : {})
      };

      console.log("🎯 Sending studentAnswer:", studentAnswerData);
      socket.current.emit("studentAnswer", studentAnswerData);
    }
  }, [selectedAnswer, studentNumber, studentId, currentQuestionIndex, currentQuestion, roomId, multiPlacements]);

// Hilfsfunktion: Warte, bis das Video bereit ist (readyState >= 2)
function waitForVideoReady(videoEl) {
  return new Promise(resolve => {
    const check = () => {
      if (videoEl.readyState >= 2) {
        resolve();
      } else {
        requestAnimationFrame(check);
      }
    };
    check();
  });
}

useEffect(() => {
  // Prüfe zuerst, ob es sich um einen Quiz-Termin handelt
  const checkAndLoadQuestions = async () => {
    if (!roomId) {
      console.error('Keine Room-ID verfügbar');
      setLoading(false);
      return;
    }
    
    try {
      // Schritt 1: Prüfe Session-Mapping
      console.log(`🔍 [Student] Prüfe Session-Typ für: ${roomId}`);
      const mappingResponse = await fetch(`https://okadia.de/lr_session_mapping_simple.php?room_id=${encodeURIComponent(roomId)}`);
      
      if (!mappingResponse.ok) {
        throw new Error(`Mapping API error! status: ${mappingResponse.status}`);
      }
      
      const mappingData = await mappingResponse.json();
      
      if (!mappingData.success) {
        throw new Error(mappingData.message || 'Session-Mapping fehlgeschlagen');
      }
      
      // Schritt 2: Wenn es keine Quiz-Session ist, keine Fragen laden
      if (!mappingData.is_quiz_session) {
        console.log(`📺 [Student] Video-Session erkannt: ${roomId} - keine Quiz-Fragen`);
        setIsVideoSession(true);
        setOwnQuestions([]); // Leere Fragen-Array für Video-Sessions
        // WICHTIG: NICHT automatisch View ändern - ViewSlider soll die Kontrolle behalten
        setLoading(false);
        return;
      }
      
      // Schritt 3: Quiz-Session - lade Fragen mit der TKursNr
      const tkursNr = mappingData.tkurs_nr;
      console.log(`📚 [Student] Quiz-Session erkannt - lade Fragen für TKursNr: ${tkursNr}`);
      
      const questionsResponse = await fetch(`https://okadia.de/lr_questions_api.php?tkurs_nr=${encodeURIComponent(tkursNr)}`);
      
      if (!questionsResponse.ok) {
        throw new Error(`Quiz API error! status: ${questionsResponse.status}`);
      }
      
      const questionsData = await questionsResponse.json();
      
      if (questionsData.success && questionsData.questions && questionsData.questions.length > 0) {
        console.log(`✅ ${questionsData.questions.length} Quiz-Fragen für TKursNr ${tkursNr} geladen`);
        setOwnQuestions(questionsData.questions);
        
        // --- Alle Bilder aus Fragen vorladen ---
        const allImages = [];
        questionsData.questions.forEach(q => {
          if (q.image) allImages.push(q.image);
          if (q.dropTargets) q.dropTargets.forEach(t => t.image && allImages.push(t.image));
          if (q.pieces) q.pieces.forEach(p => p.image && allImages.push(p.image));
        });
        [...new Set(allImages)].forEach(src => {
          const img = new window.Image();
          img.src = src;
        });
        
      } else {
        console.warn(`⚠️ Keine Fragen für TKursNr ${tkursNr} ausgewählt:`, questionsData.message);
        setOwnQuestions([]);
      }

      setLoading(false);
    } catch (error) {
      console.error('❌ Fehler beim Laden der Session-Daten:', error);

      setOwnQuestions([]);
      setLoading(false);
    }
  };

  checkAndLoadQuestions();
}, [roomId]);

// Optional (robuster): Setze modStreamConsumed zurück, wenn roomId sich ändert
useEffect(() => {
  setModStreamConsumed(false);
  modStreamConsumedRef.current = false; // Auch Ref zurücksetzen
}, [roomId]);

useEffect(() => {
  // Warte auf Authentifizierung UND Device-Check bevor MediaSoup startet
  if (!authValidated) {
    console.log(`🔍 [DEBUG] MediaSoup useEffect - warte auf Authentifizierung: authValidated=${authValidated}`);
    return;
  }

  if (!deviceCheckComplete) {
    console.log('🔍 [DEBUG] MediaSoup useEffect - warte auf Device-Check completion');
    return;
  }

  // Observer: keine getUserMedia – direkt zur MediaSoup-Socket-Verbindung (nur Consume)
  if (clientRole === 'observer') {
    console.log("👁️ Observer-Modus: Überspringe getUserMedia/produce – starte nur Consume-Pfad");
    return setupMediasoupConsumeOnly();
  }

  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    console.error("❌ getUserMedia ist nicht verfügbar. Der Browser unterstützt kein WebRTC oder es wird nicht über HTTPS geladen.");
    return;
  }

  console.log("🎥 Versuche Kamera zu starten...");

  // Holt einen frischen Video-Track und wiederholt bei TRANSIENTEN Fehlern (Kamera kurz
  // von einer anderen App/Tab belegt → NotReadableError/AbortError). Bei dauerhaften
  // Fehlern (Berechtigung verweigert, keine Kamera) wird SOFORT aufgegeben – Retry wäre
  // sinnlos. Genutzt beim Erststart (getrennter Fallback) UND bei der Track-Recovery.
  const acquireVideoTrack = async ({ maxAttempts = 5 } = {}) => {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const s = await navigator.mediaDevices.getUserMedia({ video: true });
        return s.getVideoTracks()[0] || null;
      } catch (e) {
        if (!isTransientCameraError(e) || attempt === maxAttempts) {
          console.error(`[VIDEO-HEALTH] Kamera nicht verfügbar (${e.name}: ${e.message}) – gebe auf nach Versuch ${attempt}.`);
          return null;
        }
        const delay = Math.min(700 * 2 ** (attempt - 1), 5000);
        console.warn(`[VIDEO-HEALTH] Kamera belegt (${e.name}), erneuter Versuch ${attempt + 1}/${maxAttempts} in ${delay}ms…`);
        await new Promise((r) => setTimeout(r, delay));
      }
    }
    return null;
  };

  // Überwacht den Video-Track eines Producers und stellt ihn bei unerwartetem Ende
  // wieder her (replaceTrack in denselben Producer + Selbstansicht aktualisieren).
  const attachVideoRecovery = (producer) => {
    let recovering = false;
    const wire = (track) => {
      if (!track) return;
      const onEnded = async () => {
        if (recovering || producer.closed) return;
        if (!shouldRecoverTrack({ readyState: track.readyState, intentional: false })) return;
        recovering = true;
        console.warn('[VIDEO-HEALTH] Kamera-Track unerwartet beendet – starte Recovery…');
        const fresh = await acquireVideoTrack();
        if (!fresh) {
          console.error('[VIDEO-HEALTH] Recovery fehlgeschlagen – Kamera dauerhaft nicht verfügbar. Schüler bleibt bei Audio.');
          recovering = false;
          return;
        }
        try {
          await producer.replaceTrack({ track: fresh });
          if (window.localCameraStream) {
            window.localCameraStream.getVideoTracks().forEach((t) => window.localCameraStream.removeTrack(t));
            window.localCameraStream.addTrack(fresh);
          }
          if (studentSelfVideoRef.current) {
            studentSelfVideoRef.current.srcObject = window.localCameraStream;
            studentSelfVideoRef.current.play().catch(() => {});
          }
          console.log('[VIDEO-HEALTH] Recovery erfolgreich – Video läuft wieder.');
          wire(fresh); // auch den neuen Track überwachen
        } catch (e) {
          console.error(`[VIDEO-HEALTH] replaceTrack nach Recovery fehlgeschlagen: ${e.message}`);
        } finally {
          recovering = false;
        }
      };
      track.addEventListener('ended', onEnded);
      // 'mute' ist oft nur temporär (Track erholt sich per 'unmute' von selbst) → nur beobachten.
      track.addEventListener('mute', () => console.warn('[VIDEO-HEALTH] Kamera-Track stumm (mute) – beobachte…'));
      track.addEventListener('unmute', () => console.log('[VIDEO-HEALTH] Kamera-Track wieder aktiv (unmute).'));
    };
    wire(producer.track);
  };

  // Safari (macOS/iOS) lässt die kombinierte {video,audio}-Anfrage gelegentlich komplett
  // scheitern (z.B. Kamera durch andere App belegt → NotReadableError, oder OverconstrainedError).
  // Früher fiel der Code dann STILL auf reines Audio zurück ({video:false}) → der Schüler
  // erzeugte nie einen Video-Producer, der Moderator sah kein Video. Deshalb jetzt: bei
  // Fehlschlag Video UND Audio GETRENNT anfordern und zusammenführen. So killt ein
  // Kameraproblem nicht das Audio, ein kombinierter Safari-Bug nicht das Video – und wenn die
  // Kamera wirklich nicht startet, steht der konkrete Grund klar in der Konsole.
  (async () => {
    try {
      return await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
    } catch (err) {
      console.warn(`⚠️ Kombinierte Kamera+Mikro-Anfrage fehlgeschlagen (${err.name}: ${err.message}). Fordere Video und Audio getrennt an...`);
      let videoTrack = null, audioTrack = null;
      videoTrack = await acquireVideoTrack();
      if (!videoTrack) {
        console.error(`❌ Kamera konnte auch getrennt (mit Wiederholungen) nicht gestartet werden – Schüler sendet vorerst nur Audio. Ist die Kamera von einer anderen App (Zoom/FaceTime/Photo Booth) belegt oder die Berechtigung verweigert?`);
      }
      try {
        const a = await navigator.mediaDevices.getUserMedia({ audio: true });
        audioTrack = a.getAudioTracks()[0] || null;
      } catch (e) {
        console.error(`❌ Mikrofon konnte nicht gestartet werden (${e.name}: ${e.message}).`);
      }
      const combined = new MediaStream();
      if (videoTrack) combined.addTrack(videoTrack);
      if (audioTrack) combined.addTrack(audioTrack);
      return combined;
    }
  })()
    .then((stream) => {
      console.log("✅ Kamera-Stream erhalten");

      // Speichere Stream global für spätere Wiederverwendung
      window.localCameraStream = stream;

      // Warte kurz, damit die DOM-Elemente vollständig gerendert sind
      setTimeout(() => {
        // Weise Stream für Selbstansicht zu (mit ref statt querySelector)
        if (studentSelfVideoRef.current) {
          console.log("📷 Weise Stream zu Selbstansicht-Video zu (ref)");
          studentSelfVideoRef.current.srcObject = stream;
          studentSelfVideoRef.current.play().catch(err => console.warn("⚠️ Selbstansicht-Video Autoplay fehlgeschlagen:", err));
        } else {
          console.warn("⚠️ studentSelfVideoRef ist nicht verfügbar - versuche querySelector als Fallback");
          const fallbackVideo = document.querySelector('.studentSelfVideo');
          if (fallbackVideo) {
            console.log("📷 Fallback: Weise Stream zu Selbstansicht-Video zu (querySelector)");
            fallbackVideo.srcObject = stream;
            fallbackVideo.play().catch(err => console.warn("⚠️ Fallback Selbstansicht-Video Autoplay fehlgeschlagen:", err));
          }
        }
      }, 100); // 100ms Verzögerung

      if (!socket.current) return;

      // ---- Mediasoup Integration ----
      // MediaSoup-Verbindung über Port 443 / Pfad /mediasoup/ (wie der Moderator).
      // Früher: Port :3005 — wird in vielen Schul-/Firmennetzen ausgehend blockiert,
      // sodass der Schüler keine MediaSoup-Verbindung aufbauen konnte (kein
      // Producer/Consumer → Mod sieht/hört Schüler nicht und umgekehrt).
      const mediasoupUrl = 'https://rooms.okadia.de';
      console.log('🔧 MediaSoup setup - ERSTELLE SEPARATE SOCKET für MediaSoup');
      
      const mediasoupSocket = io(mediasoupUrl, {
        path: '/mediasoup/socket.io/',
        transports: ['websocket', 'polling'],
        upgrade: true,
        timeout: 20000,
        auth: {
          token: authToken,
          roomId: roomId || "defaultRoom",
          role: clientRole
        }
      });

      // FIX: Race-Condition – verhindert, dass modStream-Consume (recv-Transport)
      // konkurrent mit dem Produce (send-Transport) aufgesetzt wird.
      // In Safari schlägt das Produce mit "Failed to set recv parameters for m-section
      // with mid='0'" fehl, wenn beide SDP-Verhandlungen gleichzeitig laufen.
      let producerSetupDone = false;
      let onProducerSetupDone = null;
      // Queue für studentAudioStream-Events die ankommen bevor das Device bereit ist
      const pendingStudentAudioStreams = [];
      // Track whether this is a reconnect (to reset the modStream guard)
      let hasConnectedBefore = false;
      
      mediasoupSocket.on('connect', () => {
        console.log("✅ MediaSoup-Socket verbunden:", mediasoupSocket.id);

        // On reconnect: reset state so the student can re-join and re-consume
        if (hasConnectedBefore) {
          console.log("🔄 MediaSoup-Socket reconnect erkannt – setze modStream-Guard zurück");
          modStreamConsumedRef.current = false;
          setModStreamConsumed(false);
          producerSetupDone = false;
          onProducerSetupDone = null;
        }
        hasConnectedBefore = true;
        
        // Setup MediaSoup event handlers auf der MediaSoup-Socket
        console.log(`🧠 Mediasoup joinRoom wird gesendet mit role: student`);
        
        // Speichere MediaSoup Socket für späteren Gebrauch
        window.mediasoupSocket = mediasoupSocket;
        
        // Sende joinRoom für MediaSoup-Connection (für Video-Streaming)
        const joinData = {
          roomId: roomId || "defaultRoom",
          role: clientRole
        };
        
        // Füge Namen hinzu wenn bereits verfügbar
        if (authUser?.name) {
          joinData.studentName = authUser.name;
          console.log(`🧠 Sende joinRoom mit Namen: ${authUser.name}`);
        } else {
          console.log(`🧠 Sende joinRoom ohne Namen - wird später nachgesendet`);
        }
        
        mediasoupSocket.emit("joinRoom", joinData);
        console.log(`🧠 Mediasoup joinRoom wurde gesendet`);

        // Producer-Setup NACH joinRoom starten: garantiert, dass socket.data.roomId
        // auf dem Server gesetzt ist, bevor getRtpCapabilities / produce eintreffen.
        setupProducer();
        // Aktiv alle bereits vorhandenen Streams anfordern (unabhängig vom Event-Timing).
        mediasoupSocket.emit("requestStreams");
      });
      
      mediasoupSocket.on('connect_error', (error) => {
        console.error('❌ MediaSoup Socket connection error:', error);
      });
      
      // Reagiert auf ICE-/Connection-Abbrüche mit einem ICE-Restart statt das Stream sterben zu lassen.
      const attachIceRestart = (transport, socket) => {
        let restarting = false;
        const tryRestart = async (state) => {
          if (restarting) return;
          if (state !== 'disconnected' && state !== 'failed') return;
          restarting = true;
          try {
            const { iceParameters } = await emitAsync(socket, 'restartIce', { transportId: transport.id });
            if (iceParameters) {
              await transport.restartIce({ iceParameters });
              console.log(`🧊 ICE-Restart ausgeführt für Transport ${transport.id}`);
            }
          } catch (e) {
            console.error('❌ ICE-Restart fehlgeschlagen:', e.message);
          } finally {
            restarting = false;
          }
        };
        transport.on('connectionstatechange', tryRestart);
        transport.on('icestatechange', tryRestart);
      };

      // --- modStream-Event Listener: Consume mit Retry/Backoff ---
      mediasoupSocket.on("modStream", () => {
        console.log("[CLIENT] modStream-Event empfangen!");
        if (modStreamConsumedRef.current) return; // bereits erfolgreich konsumiert
        setModStreamAvailable(true);

        const startModStreamConsume = async () => {
          if (!deviceRef.current) {
            console.warn("⚠️ modStream: deviceRef noch nicht gesetzt – überspringe Consume");
            return;
          }
          const iceServers = await getIceServers(authToken);

          await withRetry(async () => {
            const { params: recvTransportOptions } = await emitAsync(
              mediasoupSocket, "createWebRtcTransport", { direction: "recv" }
            );
            const recvTransport = deviceRef.current.createRecvTransport({ ...recvTransportOptions, iceServers });

            try {
              recvTransport.on("connect", ({ dtlsParameters }, callback, errback) => {
                mediasoupSocket.emit("connectTransport", {
                  transportId: recvTransport.id, dtlsParameters, direction: "recv"
                }, (response) => (response === 'connected' ? callback() : errback(new Error('connectTransport fehlgeschlagen'))));
              });

              attachIceRestart(recvTransport, mediasoupSocket);

              const { consumers } = await emitAsync(mediasoupSocket, "consume", {
                rtpCapabilities: deviceRef.current.rtpCapabilities,
                transportId: recvTransport.id
              });
              if (!Array.isArray(consumers) || consumers.length === 0) {
                throw new Error('Keine Consumer erhalten');
              }
              for (const { id, kind, producerId, rtpParameters, appData } of consumers) {
                const consumer = await recvTransport.consume({ id, producerId, kind, rtpParameters });
                const stream = new MediaStream([consumer.track]);
                if (appData && appData.role === "mod" && kind === "video") {
                  window.modVideoStream = stream;
                  if (modPreviewRef.current) { modPreviewRef.current.srcObject = stream; modPreviewRef.current.play().catch(() => {}); }
                } else if (appData && appData.role === "mod" && kind === "audio") {
                  window.modAudioStream = stream;
                  setModAudioAvailable(true);
                  setAudioJoined(true);
                  const audioEl = document.getElementById("modAudio");
                  if (audioEl) { audioEl.srcObject = stream; audioEl.play().catch(() => {}); }
                }
              }
              // Erst NACH erfolgreichem Consume als erledigt markieren.
              modStreamConsumedRef.current = true;
              setModStreamConsumed(true);
              console.log("✅ modStream erfolgreich konsumiert");
            } catch (err) {
              try { recvTransport.close(); } catch (e) { /* ignore */ }
              throw err;
            }
          }, { label: 'modStream-consume', retries: 5, base: 500, max: 8000 })
          .catch((e) => console.error("❌ modStream-Consume endgültig fehlgeschlagen:", e.message));
        };

        if (producerSetupDone) startModStreamConsume();
        else onProducerSetupDone = startModStreamConsume;
      });
      
      // Hilfsfunktion: Konsumiere Audio eines anderen Schülers
      const consumeStudentAudio = async ({ studentSocketId, studentNumber, studentName }) => {
        console.log(`🎧 Konsumiere Audio von Student ${studentNumber} (${studentName}), SocketId: ${studentSocketId}`);

        // Wie beim modStream-Consume (siehe producerSetupDone oben): Ein recv-Transport darf
        // in Safari nicht parallel zur eigenen Produce-Verhandlung aufgebaut werden, sonst
        // korrumpiert das die laufende SDP-Verhandlung ("Failed to set recv parameters for
        // m-section mit mid='0'") - das Video wird dann lautlos kaputt (Audio bleibt ok).
        // Daher: genau wie bei fehlendem Device in die Queue stellen und nach Produce-Setup nachholen.
        if (!deviceRef.current || !producerSetupDone) {
          console.warn("❌ Device/Producer-Setup noch nicht abgeschlossen für studentAudioStream - in Queue gespeichert");
          pendingStudentAudioStreams.push({ studentSocketId, studentNumber, studentName });
          return;
        }
        
        try {
          // Erstelle neuen Receive-Transport für diesen Student-Audio-Stream
          mediasoupSocket.emit("createWebRtcTransport", { direction: "recv" }, async ({ params: recvTransportOptions }) => {
            const iceServers = await getIceServers(authToken);
            const recvTransport = deviceRef.current.createRecvTransport({ ...recvTransportOptions, iceServers });

            recvTransport.on("connect", ({ dtlsParameters }, callback, errback) => {
              mediasoupSocket.emit("connectTransport", {
                transportId: recvTransport.id,
                dtlsParameters,
                direction: "recv"
              }, (response) => {
                if (response === 'connected') {
                  callback();
                } else {
                  console.error("❌ Fehler bei connectTransport (studentAudio)", response);
                  errback();
                }
              });
            });

            attachIceRestart(recvTransport, mediasoupSocket);

            // Konsumiere Audio von diesem Schüler
            mediasoupSocket.emit("consume", {
              rtpCapabilities: deviceRef.current.rtpCapabilities,
              streamType: 'student',
              sourceSocketId: studentSocketId,
              transportId: recvTransport.id
            }, async (response) => {
              console.log(`🔍 Student-Audio consume response:`, response);
              
              if (!response || !Array.isArray(response.consumers)) {
                console.warn("❌ Ungültige Consumer-Antwort für student audio:", response);
                return;
              }
              
              // Verarbeite ALLE Consumer (video UND audio), um SDP-Konsistenz sicherzustellen.
              // Alle Consumer müssen consumed werden damit der Transport-SDP vollständig ist;
              // Audio-Tracks werden abgespielt, Video-Tracks von anderen Schülern werden verworfen.
              for (const consumerData of response.consumers) {
                const { id, kind, rtpParameters, producerId } = consumerData;
                
                if (!id || !rtpParameters) {
                  console.warn("❌ Ungültiger Consumer:", consumerData);
                  continue;
                }
                
                const consumer = await recvTransport.consume({
                  id,
                  producerId,
                  kind,
                  rtpParameters
                });

                if (kind === 'audio') {
                  const stream = new MediaStream([consumer.track]);
                  // Audio-Element erstellen und abspielen
                  const audio = new Audio();
                  audio.srcObject = stream;
                  audio.play().catch(e => console.warn(`🔇 Autoplay blockiert für Student ${studentNumber}`, e));
                  peerAudioRefs.current.push(audio); // für Cleanup auf Pokalseite
                  console.log(`✅ Audio von Student ${studentNumber} (${studentName}) wird jetzt wiedergegeben`);
                } else {
                  // Video-Track von anderen Schülern wird nicht angezeigt (nur consumed für SDP-Vollständigkeit)
                  console.log(`📹 Video-Consumer für Student ${studentNumber} (${kind}) consumed aber nicht angezeigt`);
                }
              }
            });
          });
        } catch (error) {
          console.error(`❌ Fehler beim Konsumieren von Student ${studentNumber} Audio:`, error);
        }
      };

      // ✅ Event-Handler für Audio-Streams von anderen Schülern
      mediasoupSocket.on("studentAudioStream", (data) => {
        consumeStudentAudio(data);
      });

      // ✅ Hospitations-Icon: Observer-Events
      mediasoupSocket.on('observerPresent', ({ count }) => dispatchObserver({ type: 'observerPresent', count }));
      mediasoupSocket.on('observerLeft',    ({ count }) => dispatchObserver({ type: 'observerLeft', count }));

      // setupProducer wird vom connect-Handler aufgerufen, nachdem joinRoom gesendet wurde.
      // function-Deklaration wird im umschließenden Scope hochgehoben (Hoisting).
      function setupProducer() {
        mediasoupSocket.emit("getRtpCapabilities", {}, (data) => {
          const setupMediasoup = async () => {
            const device = new mediasoupClient.Device();
          const rtpCapabilities = data?.rtpCapabilities || data;
          await device.load({ routerRtpCapabilities: rtpCapabilities });
          deviceRef.current = device;

          console.log("📡 Transport wird erstellt...");
          mediasoupSocket.emit("createWebRtcTransport", { direction: "send" }, async ({ params: transportOptions }) => {
            const iceServers = await getIceServers(authToken);
            const transport = device.createSendTransport({ ...transportOptions, iceServers });

            transport.on("connect", ({ dtlsParameters }, callback, errback) => {
              console.log("📨 Sende connectTransport mit ID:", transport.id);
              mediasoupSocket.emit("connectTransport", {
                transportId: transport.id,
                dtlsParameters,
                direction: "send"
              }, (response) => {
                if (response === 'connected') {
                  callback();
                } else {
                  console.error("❌ Fehler bei connectTransport", response);
                  errback();
                }
              });
            });

            attachIceRestart(transport, mediasoupSocket);

            transport.on("produce", ({ kind, rtpParameters }, callback, errback) => {
              console.log("🔗 Sending produce request:", { transportId: transport.id, kind, rtpParameters: !!rtpParameters });
              mediasoupSocket.emit("produce", { 
                transportId: transport.id,
                kind, 
                rtpParameters 
              }, (response) => {
                console.log("🔍 Produce response:", response);
                if (response && response.id) {
                  callback({ id: response.id });
                } else if (response && response.error) {
                  console.error("❌ Server-Fehler beim Produzieren:", response.error);
                  errback(new Error(response.error));
                } else {
                  console.error("❌ Unbekannter Fehler beim Produzieren:", response);
                  errback(new Error("Unknown produce error"));
                }
              });
            });

            sendTransportRef.current = transport;

            console.log("🎥 Video wird produziert...");
            try {
              const videoTrack = stream.getVideoTracks()[0];
              if (videoTrack) {
                console.log("📹 Video-Track gefunden, produziere...");
                const videoProducer = await transport.produce({ track: videoTrack });
                console.log("✅ Video erfolgreich produziert, ID:", videoProducer.id);
                // Härtung: stirbt der Kamera-Track später unerwartet (iPad im Hintergrund,
                // OS/andere App entzieht die Kamera), wird er automatisch neu geholt und per
                // replaceTrack in DENSELBEN Producer eingespeist – ohne Neuverhandlung. Ohne
                // das blieb der Producer verwaist und der Moderator bekam nur noch Audio.
                attachVideoRecovery(videoProducer);
              } else {
                console.warn("⚠️ Kein Videotrack gefunden");
              }
              
              // --- Audio-Track produzieren ---
              const audioTrack = stream.getAudioTracks()[0];
              if (audioTrack) {
                console.log("🎤 Audio-Track gefunden, produziere...");
                const audioProducer = await transport.produce({ track: audioTrack });
                console.log("✅ Audio erfolgreich produziert, ID:", audioProducer.id);
              } else {
                console.warn("⚠️ Kein Audiotrack gefunden");
              }
              
              console.log("✅ Video und Audio erfolgreich produziert - MediaSoup wird automatisch studentStream-Event senden");
              
            } catch (err) {
              console.error("❌ Fehler beim Produzieren des Videos:", {
                message: err.message,
                name: err.name,
                stack: err.stack,
                error: err
              });
            }

            // FIX: Producer-Setup abgeschlossen – jetzt ggf. aufgeschobenes modStream-Consume starten
            producerSetupDone = true;
            if (onProducerSetupDone) {
              console.log("🔔 Producer-Setup abgeschlossen – starte aufgeschobenes modStream-Consume");
              const fn = onProducerSetupDone;
              onProducerSetupDone = null;
              fn();
            }

            // Queue abarbeiten: studentAudioStream-Events die vor Device-Init ankamen
            if (pendingStudentAudioStreams.length > 0) {
              console.log(`🎧 Verarbeite ${pendingStudentAudioStreams.length} gepufferte studentAudioStream-Events`);
              const pending = pendingStudentAudioStreams.splice(0);
              pending.forEach(data => consumeStudentAudio(data));
            }

            mediasoupSocket.emit("newProducer", { roomId: roomId || "defaultRoom" });

            // --- Add: Listen for newProducer event and consume remote streams ---
            mediasoupSocket.on("newProducer", async ({ producerId, kind, role }) => {
              console.log("🎥 newProducer empfangen:", producerId, kind, role);
              // Für student-Stream: wie bisher
              if (kind === "video" && role === "student") {
                setModStreamConsumed(false); // Erlaube Neuempfang des Schülerstreams
              }
              // Für mod-Stream: neuen Transport anlegen und konsumieren wie bei consume-Ablauf
              if (role === "mod") {
                mediasoupSocket.emit("createWebRtcTransport", { direction: "recv" }, async ({ params: recvTransportOptions }) => {
                  const iceServers = await getIceServers(authToken);
                  const recvTransport = deviceRef.current.createRecvTransport({ ...recvTransportOptions, iceServers });
                  recvTransport.on("connect", ({ dtlsParameters }, callback, errback) => {
                    mediasoupSocket.emit("connectTransport", {
                      transportId: recvTransport.id,
                      dtlsParameters,
                      direction: "recv"
                    }, (response) => {
                      if (response === 'connected') {
                        callback();
                      } else {
                        console.error("❌ Fehler bei connectTransport (recv)", response);
                        errback();
                      }
                    });
                  });

                  attachIceRestart(recvTransport, mediasoupSocket);

                  mediasoupSocket.emit("consume", {
                    producerId,
                    rtpCapabilities: deviceRef.current.rtpCapabilities,
                    transportId: recvTransport.id
                  }, async (response) => {
                    console.log("🔍 Consume response:", response);
                    
                    // Server gibt {consumers: Array} zurück
                    if (!response || !Array.isArray(response.consumers)) {
                      console.warn("❌ Ungültige Consumer-Antwort:", response);
                      return;
                    }
                    
                    // Verarbeite alle Consumer aus der Antwort
                    for (const consumerData of response.consumers) {
                      const { id, kind: consumedKind, rtpParameters, producerId: consumerProducerId } = consumerData;
                      
                      // Nur Consumer für den aktuellen Producer verarbeiten
                      if (consumerProducerId !== producerId) {
                        continue;
                      }
                      
                      if (!id || !rtpParameters) {
                        console.warn("❌ Ungültiger Consumer:", consumerData);
                        continue;
                      }
                      
                      const consumer = await recvTransport.consume({
                        id,
                        producerId,
                        kind: consumedKind,
                        rtpParameters
                      });
                      
                      const stream = new MediaStream([consumer.track]);
                      
                      if (consumedKind === "video") {
                        if (modVideoRef.current) {
                          modVideoRef.current.srcObject = stream;
                          waitForVideoReady(modVideoRef.current).then(() => {
                            modVideoRef.current.play().catch(() => {});
                          });
                          console.log("Mod-Video-Stream empfangen und dem DOM zugewiesen.");
                        }
                      }
                      
                      if (consumedKind === "audio") {
                        // Optional: Audio-Handling für Mod
                        window.modAudioStream = stream;
                        setModAudioAvailable(true);
                      }
                    }
                  });
                });
                return; // Nur für mod behandeln; student geht weiter wie unten
              }
              // Bisherige Logik für andere Rollen (nur Audio für student)
              mediasoupSocket.emit("createWebRtcTransport", { direction: "recv" }, async ({ params: recvTransportOptions }) => {
                const iceServers = await getIceServers(authToken);
                const recvTransport = deviceRef.current.createRecvTransport({ ...recvTransportOptions, iceServers });
                recvTransport.on("connect", ({ dtlsParameters }, callback, errback) => {
                  mediasoupSocket.emit("connectTransport", {
                    transportId: recvTransport.id,
                    dtlsParameters,
                    direction: "recv"
                  }, (response) => {
                    if (response === 'connected') {
                      callback();
                    } else {
                      console.error("❌ Fehler bei connectTransport (recv)", response);
                      errback();
                    }
                  });
                });

                attachIceRestart(recvTransport, mediasoupSocket);

                mediasoupSocket.emit("consume", {
                  producerId,
                  rtpCapabilities: deviceRef.current.rtpCapabilities,
                  transportId: recvTransport.id
                }, async (response) => {
                  console.log("🔍 Consume response (2nd):", response);
                  
                  // Server gibt {consumers: Array} zurück
                  if (!response || !Array.isArray(response.consumers)) {
                    console.warn("❌ Ungültige Consumer-Antwort (2nd):", response);
                    return;
                  }
                  
                  // Verarbeite alle Consumer aus der Antwort
                  for (const consumerData of response.consumers) {
                    const { id, kind, rtpParameters, producerId: consumerProducerId } = consumerData;
                    
                    // Nur Consumer für den aktuellen Producer verarbeiten
                    if (consumerProducerId !== producerId) {
                      continue;
                    }
                    
                    if (!id || !rtpParameters) {
                      console.warn("❌ Ungültiger Consumer (2nd):", consumerData);
                      continue;
                    }
                    
                    const consumer = await recvTransport.consume({
                      id,
                      producerId,
                      kind,
                      rtpParameters
                    });

                    const stream = new MediaStream([consumer.track]);
                    // Nur Audio-Block bleibt erhalten
                    if (kind === "audio") {
                      const audio = new Audio();
                      audio.srcObject = stream;
                      audio.play().catch(e => console.warn("🔇 Autoplay blockiert", e));
                    }
                  }
                });
              });
            });
            // --- End of newProducer consumer logic ---
          });
        };
            setupMediasoup();
          }); // end getRtpCapabilities
      } // end setupProducer
    })
    .catch((err) => {
      console.error("❌ Fehler beim Zugriff auf Kamera:", err.name, err.message);
    });

  // setupMediasoupConsumeOnly: für Observer – kein getUserMedia, kein Producer-Setup.
  // Wird am Anfang des useEffect aufgerufen wenn clientRole === 'observer'.
  // eslint-disable-next-line no-inner-declarations
  function setupMediasoupConsumeOnly() {
    const mediasoupUrl = 'https://rooms.okadia.de';
    console.log('👁️ Observer: Erstelle MediaSoup-Socket (nur Consume)');
    const mediasoupSocket = io(mediasoupUrl, {
      path: '/mediasoup/socket.io/',
      transports: ['websocket', 'polling'],
      upgrade: true,
      timeout: 20000,
      auth: {
        token: authToken,
        roomId: roomId || "defaultRoom",
        role: 'observer'
      }
    });

    window.mediasoupSocket = mediasoupSocket;

    // Hilfsfunktion: ICE-Restart bei Verbindungsabbruch
    const attachIceRestartObs = (transport, sock) => {
      let restarting = false;
      const tryRestart = async (state) => {
        if (restarting) return;
        if (state !== 'disconnected' && state !== 'failed') return;
        restarting = true;
        try {
          const { iceParameters } = await emitAsync(sock, 'restartIce', { transportId: transport.id });
          if (iceParameters) await transport.restartIce({ iceParameters });
        } catch (e) {
          console.error('❌ Observer ICE-Restart fehlgeschlagen:', e.message);
        } finally { restarting = false; }
      };
      transport.on('connectionstatechange', tryRestart);
      transport.on('icestatechange', tryRestart);
    };

    // Hilfsfunktion: Erstelle Recv-Transport und konsumiere einen Stream
    const consumeViaNewTransport = async (emitArgs, onConsumers) => {
      if (!deviceRef.current) {
        console.warn('⚠️ Observer: deviceRef noch nicht gesetzt – überspringe Consume');
        return;
      }
      const iceServers = await getIceServers(authToken);
      const { params: recvOpts } = await emitAsync(mediasoupSocket, 'createWebRtcTransport', { direction: 'recv' });
      const recvTransport = deviceRef.current.createRecvTransport({ ...recvOpts, iceServers });
      recvTransport.on('connect', ({ dtlsParameters }, callback, errback) => {
        mediasoupSocket.emit('connectTransport', {
          transportId: recvTransport.id, dtlsParameters, direction: 'recv'
        }, (res) => (res === 'connected' ? callback() : errback(new Error('connectTransport fehlgeschlagen'))));
      });
      attachIceRestartObs(recvTransport, mediasoupSocket);
      const response = await emitAsync(mediasoupSocket, 'consume', {
        ...emitArgs,
        rtpCapabilities: deviceRef.current.rtpCapabilities,
        transportId: recvTransport.id
      });
      if (!response || !Array.isArray(response.consumers) || response.consumers.length === 0) {
        try { recvTransport.close(); } catch (e) { /* ignore */ }
        throw new Error('Keine Consumer erhalten');
      }
      await onConsumers(recvTransport, response.consumers);
    };

    mediasoupSocket.on('connect', () => {
      console.log('✅ Observer MediaSoup-Socket verbunden:', mediasoupSocket.id);
      const joinData = { roomId: roomId || "defaultRoom", role: 'observer' };
      if (authUser?.name) joinData.studentName = authUser.name;
      mediasoupSocket.emit('joinRoom', joinData);

      // Lade RTP-Capabilities und initialisiere Device (ohne Send-Transport)
      mediasoupSocket.emit('getRtpCapabilities', {}, async (data) => {
        try {
          const device = new mediasoupClient.Device();
          const rtpCapabilities = data?.rtpCapabilities || data;
          await device.load({ routerRtpCapabilities: rtpCapabilities });
          deviceRef.current = device;
          console.log('✅ Observer: mediasoup Device geladen');

          // Aktiv alle bereits vorhandenen Streams anfordern
          mediasoupSocket.emit('requestStreams');
        } catch (e) {
          console.error('❌ Observer: Device-Load fehlgeschlagen:', e.message);
        }
      });
    });

    mediasoupSocket.on('connect_error', (err) => {
      console.error('❌ Observer MediaSoup-Socket Verbindungsfehler:', err);
    });

    // modStream konsumieren (Moderator-Video + Audio)
    mediasoupSocket.on('modStream', () => {
      console.log('[Observer] modStream-Event empfangen');
      if (modStreamConsumedRef.current) return;
      setModStreamAvailable(true);

      withRetry(async () => {
        await consumeViaNewTransport({}, async (recvTransport, consumers) => {
          for (const { id, kind, producerId, rtpParameters, appData } of consumers) {
            const consumer = await recvTransport.consume({ id, producerId, kind, rtpParameters });
            const stream = new MediaStream([consumer.track]);
            if (appData && appData.role === 'mod' && kind === 'video') {
              window.modVideoStream = stream;
              if (modPreviewRef.current) { modPreviewRef.current.srcObject = stream; modPreviewRef.current.play().catch(() => {}); }
            } else if (appData && appData.role === 'mod' && kind === 'audio') {
              window.modAudioStream = stream;
              setModAudioAvailable(true);
              setAudioJoined(true);
              const audioEl = document.getElementById('modAudio');
              if (audioEl) { audioEl.srcObject = stream; audioEl.play().catch(() => {}); }
            }
          }
          modStreamConsumedRef.current = true;
          setModStreamConsumed(true);
          console.log('✅ Observer: modStream konsumiert');
        });
      }, { label: 'observer-modStream-consume', retries: 5, base: 500, max: 8000 })
      .catch(e => console.error('❌ Observer: modStream-Consume fehlgeschlagen:', e.message));
    });

    // studentStream konsumieren (alle Students mit Video+Audio)
    mediasoupSocket.on('studentStream', (data) => {
      const { studentSocketId } = data || {};
      console.log('[Observer] studentStream-Event für SocketId:', studentSocketId);

      withRetry(async () => {
        await consumeViaNewTransport(
          { streamType: 'student', sourceSocketId: studentSocketId },
          async (recvTransport, consumers) => {
            for (const { id, kind, producerId, rtpParameters } of consumers) {
              const consumer = await recvTransport.consume({ id, producerId, kind, rtpParameters });
              if (kind === 'video') {
                const stream = new MediaStream([consumer.track]);
                // Studentenvideo wird im Observer-Modus gespeichert (könnte in der UI angezeigt werden)
                console.log(`✅ Observer: Student-Video (${studentSocketId}) konsumiert`);
                // Video in modPreviewRef zeigen falls kein Mod-Stream vorhanden
                if (!window.modVideoStream && modPreviewRef.current) {
                  modPreviewRef.current.srcObject = stream;
                  modPreviewRef.current.play().catch(() => {});
                }
              } else if (kind === 'audio') {
                const stream = new MediaStream([consumer.track]);
                const audio = new Audio();
                audio.srcObject = stream;
                audio.play().catch(e => console.warn(`🔇 Observer: Autoplay für Student ${studentSocketId} blockiert`, e));
                peerAudioRefs.current.push(audio);
                console.log(`✅ Observer: Student-Audio (${studentSocketId}) konsumiert`);
              }
            }
          }
        );
      }, { label: `observer-studentStream-${studentSocketId}`, retries: 3, base: 500, max: 4000 })
      .catch(e => console.error(`❌ Observer: studentStream-Consume fehlgeschlagen (${studentSocketId}):`, e.message));
    });

    // studentAudioStream (dedizierter Audio-Only-Stream von Schülern)
    mediasoupSocket.on('studentAudioStream', (data) => {
      const { studentSocketId, studentNumber, studentName } = data || {};
      console.log(`[Observer] studentAudioStream für Student ${studentNumber} (${studentName})`);

      withRetry(async () => {
        await consumeViaNewTransport(
          { streamType: 'student', sourceSocketId: studentSocketId },
          async (recvTransport, consumers) => {
            for (const { id, kind, producerId, rtpParameters } of consumers) {
              const consumer = await recvTransport.consume({ id, producerId, kind, rtpParameters });
              if (kind === 'audio') {
                const stream = new MediaStream([consumer.track]);
                const audio = new Audio();
                audio.srcObject = stream;
                audio.play().catch(e => console.warn(`🔇 Observer: Autoplay für Student ${studentNumber} blockiert`, e));
                peerAudioRefs.current.push(audio);
                console.log(`✅ Observer: studentAudioStream für Student ${studentNumber} konsumiert`);
              }
            }
          }
        );
      }, { label: `observer-studentAudio-${studentSocketId}`, retries: 3, base: 500, max: 4000 })
      .catch(e => console.error(`❌ Observer: studentAudioStream-Consume fehlgeschlagen:`, e.message));
    });

    // newProducer: neuer Producer (Mod oder Student) – konsumieren
    mediasoupSocket.on('newProducer', async ({ producerId, kind, role }) => {
      console.log('[Observer] newProducer:', producerId, kind, role);
      try {
        await consumeViaNewTransport(
          { producerId },
          async (recvTransport, consumers) => {
            for (const { id, kind: consumedKind, producerId: cpId, rtpParameters } of consumers) {
              if (cpId !== producerId) continue;
              const consumer = await recvTransport.consume({ id, producerId, kind: consumedKind, rtpParameters });
              const stream = new MediaStream([consumer.track]);
              if (consumedKind === 'video' && role === 'mod') {
                window.modVideoStream = stream;
                if (modPreviewRef.current) {
                  modPreviewRef.current.srcObject = stream;
                  modPreviewRef.current.play().catch(() => {});
                }
              } else if (consumedKind === 'audio' && role === 'mod') {
                window.modAudioStream = stream;
                setModAudioAvailable(true);
                const audioEl = document.getElementById('modAudio');
                if (audioEl) { audioEl.srcObject = stream; audioEl.play().catch(() => {}); }
              } else if (consumedKind === 'audio') {
                const audio = new Audio();
                audio.srcObject = stream;
                audio.play().catch(e => console.warn('🔇 Observer: newProducer Autoplay blockiert', e));
              }
            }
          }
        );
      } catch (e) {
        console.error('[Observer] newProducer-Consume fehlgeschlagen:', e.message);
      }
    });

    // ✅ Hospitations-Icon: Observer-Events – triggern observerState-Update, Icon erscheint in allen Views via hospitationIndicator
    mediasoupSocket.on('observerPresent', ({ count }) => dispatchObserver({ type: 'observerPresent', count }));
    mediasoupSocket.on('observerLeft',    ({ count }) => dispatchObserver({ type: 'observerLeft', count }));

    return () => { mediasoupSocket.disconnect(); };
  }

}, [authValidated, roomId, deviceCheckComplete, clientRole]); // Warte auf Authentifizierung UND Device-Check

// Streams auf Pokalseite stoppen: Schüler-zu-Schüler Audio + Mod-zu-Schüler Video/Audio
useEffect(() => {
  if (currentView !== 'success') return;

  // Schüler-zu-Schüler Audio stoppen
  peerAudioRefs.current.forEach(audio => {
    audio.pause();
    audio.srcObject = null;
  });
  peerAudioRefs.current = [];

  // Mod-zu-Schüler Video stoppen
  if (modVideoRef.current) {
    modVideoRef.current.srcObject = null;
  }
  if (window.modVideoStream) {
    window.modVideoStream.getTracks().forEach(t => t.stop());
    window.modVideoStream = null;
  }

  // Mod-zu-Schüler Audio stoppen
  const modAudioEl = document.getElementById('modAudio');
  if (modAudioEl) {
    modAudioEl.pause();
    modAudioEl.srcObject = null;
  }
  if (window.modAudioStream) {
    window.modAudioStream.getTracks().forEach(t => t.stop());
    window.modAudioStream = null;
  }
}, [currentView]); // eslint-disable-line react-hooks/exhaustive-deps

useEffect(() => {
  console.log('📹 Video Stream Update triggered by view change:', currentView);

  // Moderator Video Stream wiederherstellen - nur wenn noch nicht zugewiesen:
  // Erneutes Setzen von srcObject auf denselben Stream startet das Laden neu und
  // bricht ein noch laufendes play() mit einem AbortError ab (siehe Self-Video unten).
  if (window.modVideoStream && modPreviewRef.current && modPreviewRef.current.srcObject !== window.modVideoStream) {
    console.log('📹 Restoring moderator video stream for view:', currentView);
    modPreviewRef.current.srcObject = window.modVideoStream;
    waitForVideoReady(modPreviewRef.current).then(() => {
      modPreviewRef.current.play().catch(() => {});
    });
  }

  // Student Self Video Stream wiederherstellen - nur wenn noch nicht zugewiesen.
  // Dieser Effekt feuert bei jeder Aenderung von modStreamAvailable (z.B. wenn das
  // modStream-Event eintrifft), auch wenn sich currentView nicht geaendert hat. Ohne
  // den Vergleich wurde der bereits laufende, vom Kamerastart gestartete play()-Aufruf
  // durch das erneute Setzen von srcObject abgebrochen (AbortError) - die Selbstansicht
  // blieb dann dauerhaft leer.
  if (window.localCameraStream && studentSelfVideoRef.current && studentSelfVideoRef.current.srcObject !== window.localCameraStream) {
    console.log('📹 Restoring student self video stream for view:', currentView);
    studentSelfVideoRef.current.srcObject = window.localCameraStream;
    studentSelfVideoRef.current.play().catch(() => {});
  }

  // Moderator Audio Stream wiederherstellen – nach kurzem Delay damit das neue
  // Audio-Element von React zuerst in den DOM gemountet wird
  if (window.modAudioStream) {
    setTimeout(() => {
      const audioEl = document.getElementById("modAudio");
      if (audioEl) {
        console.log('🔊 Restoring moderator audio stream for view:', currentView);
        audioEl.srcObject = window.modAudioStream;
        audioEl.play().catch(e => console.log('🔇 Audio restore play failed:', e));
      }
    }, 150);
  }
}, [currentView, isQuizStarted, modStreamAvailable]);

// === Ensure video streams are always attached to video elements ===
useEffect(() => {
  // Kleine Verzögerung um sicherzustellen, dass DOM-Elemente existieren
  const timeout = setTimeout(() => {
    // Moderator Video wiederherstellen (für alle Views)
    if (window.modVideoStream && modPreviewRef.current && !modPreviewRef.current.srcObject) {
      console.log('🔄 Re-attaching moderator video stream after view change');
      modPreviewRef.current.srcObject = window.modVideoStream;
      modPreviewRef.current.play().catch(() => {});
    }

    // Student Self Video wiederherstellen (für alle Views)
    if (window.localCameraStream && studentSelfVideoRef.current && !studentSelfVideoRef.current.srcObject) {
      console.log('🔄 Re-attaching student self video stream after view change');
      studentSelfVideoRef.current.srcObject = window.localCameraStream;
      studentSelfVideoRef.current.play().catch(() => {});
    }
  }, 100);

  return () => clearTimeout(timeout);
}, [currentView]);

// Stream-Recovery nur wenn Fragen von leer→befüllt wechseln (Quiz-View wird neu gemountet).
// NICHT bei jedem questions-Update ausführen – sonst entstehen Audio-Glitches.
useEffect(() => {
  const wasEmpty = prevQuestionsLengthRef.current === 0;
  prevQuestionsLengthRef.current = questions.length;
  if (!wasEmpty) return;

  if (window.modVideoStream && modPreviewRef.current && !modPreviewRef.current.srcObject) {
    console.log('🔄 [Recovery] Moderator-Stream nach Fragen-Erstladung anhängen');
    modPreviewRef.current.srcObject = window.modVideoStream;
    modPreviewRef.current.play().catch(() => {});
  }
  if (window.localCameraStream && studentSelfVideoRef.current && !studentSelfVideoRef.current.srcObject) {
    console.log('🔄 [Recovery] Self-Stream nach Fragen-Erstladung anhängen');
    studentSelfVideoRef.current.srcObject = window.localCameraStream;
    studentSelfVideoRef.current.play().catch(() => {});
  }
}, [questions]);

// --- NEU: Mod-Audio-Element regelmäßig verbinden, wenn im Warteraum ---
useEffect(() => {
  let interval;
  if (!isQuizStarted && audioJoined && modAudioAvailable) {
    interval = setInterval(() => {
      const audioEl = document.getElementById("modAudio");
      if (audioEl && window.modAudioStream) {
        if (audioEl.srcObject !== window.modAudioStream) {
          audioEl.srcObject = window.modAudioStream;
          audioEl.play()
            .then(() => console.log("Play attempt SUCCESS", Date.now()))
            .catch(e => console.log("Play attempt FAILED", Date.now(), e));
        } else {
          audioEl.play()
            .then(() => console.log("Play attempt REPEAT", Date.now()))
            .catch(e => console.log("Play attempt REPEAT FAILED", Date.now(), e));
        }
      }
    }, 200);
  }
  return () => clearInterval(interval);
}, [isQuizStarted, audioJoined, modAudioAvailable]);

// Mod-Audio automatisch verbinden, wenn verfügbar
useEffect(() => {
  if (modAudioAvailable && audioJoined && window.modAudioStream) {
    const audioEl = document.getElementById("modAudio");
    if (audioEl) {
      audioEl.srcObject = window.modAudioStream;
      audioEl.play()
        .then(() => console.log("Play from modAudioAvailable SUCCESS", Date.now()))
        .catch(e => console.log("Play from modAudioAvailable FAILED", Date.now(), e));
    }
  }
}, [modAudioAvailable, audioJoined, isQuizStarted]);

// Dauerhafter Stream-Watchdog: prüft alle 3s ob Streams noch laufen
useEffect(() => {
  const watchdog = setInterval(() => {
    if (window.modVideoStream && modPreviewRef.current && !modPreviewRef.current.srcObject) {
      console.log('🐕 [Watchdog] Moderator-Video verloren - wird wiederhergestellt');
      modPreviewRef.current.srcObject = window.modVideoStream;
      modPreviewRef.current.play().catch(() => {});
    }
    if (window.localCameraStream && studentSelfVideoRef.current && !studentSelfVideoRef.current.srcObject) {
      console.log('🐕 [Watchdog] Self-Stream verloren - wird wiederhergestellt');
      studentSelfVideoRef.current.srcObject = window.localCameraStream;
      studentSelfVideoRef.current.play().catch(() => {});
    }
    // Audio-Watchdog: stellt sicher, dass das Audio-Element immer den Stream hat
    if (window.modAudioStream) {
      const audioEl = document.getElementById("modAudio");
      if (audioEl && audioEl.srcObject !== window.modAudioStream) {
        console.log('🐕 [Watchdog] Moderator-Audio verloren - wird wiederhergestellt');
        audioEl.srcObject = window.modAudioStream;
        audioEl.play().catch(() => {});
      }
    }
  }, 3000);
  return () => clearInterval(watchdog);
}, []);

  // multiDragdrop: automatische Auswertung sobald alle Teile platziert sind
  useEffect(() => {
    if (currentQuestion?.type !== "multiDragdrop") return;
    if (selectedAnswer !== null) return;
    if (!currentQuestion.pieces) return;
    if (allPlaced(multiPlacements, currentQuestion.pieces)) {
      const isCorrect = allCorrect(multiPlacements);
      // gespiegelt aus finishComplex — bei Änderung dort hier synchron halten
      setSelectedAnswer(isCorrect ? "correct" : "wrong");
      if (isCorrect) setCorrectAnswers((prev) => prev + 1);
      // answeredQuestions wird vom Emit-useEffect (auf selectedAnswer) gesetzt, nicht hier
      setTimeout(() => setShowNextButton(true), 800);
    }
  }, [multiPlacements, currentQuestion, selectedAnswer]);

  // ✅ Warte auf Token-Validierung UND darauf, dass der Logo-Aufbau der
  //   Buch-Ladeanimation mindestens einmal komplett gezeigt wurde. So wird der
  //   Aufbau nie abgeschnitten, auch wenn die Auth sehr schnell fertig ist.
  if (!authValidated || !loaderMinShown) {
    return <BookLoaderScreen onDrawn={() => setLoaderMinShown(true)} />;
  }

  // ✅ Nach Token-Validierung: Device-Check
  return (
    <DeviceBlocker onDeviceCheckComplete={handleDeviceCheckComplete}>
      {renderMainContent()}
    </DeviceBlocker>
  );

  function renderMainContent() {

  // === Observer: Audio-Autoplay per Nutzer-Geste freischalten ===
  // Der Observer ruft nie getUserMedia auf → keine Nutzer-Geste im Tab → der Browser
  // blockiert die Audio-Wiedergabe (Bild kommt an, Ton nicht). Ein Klick hebt die Sperre
  // für den gesamten Tab auf und spielt bereits konsumierte Audio-Streams ab.
  const unlockObserverAudio = () => {
    try {
      const modA = document.getElementById('modAudio');
      if (modA) { modA.muted = false; modA.play().catch(() => {}); }
      peerAudioRefs.current.forEach(a => { try { a.muted = false; a.play().catch(() => {}); } catch (e) { /* ignore */ } });
    } catch (e) { /* ignore */ }
    setObserverAudioUnlocked(true);
  };

  // === Ansichts-unabhängige Overlays (position: fixed) ===
  const hospitationIndicator = (
    <>
      {isObserverVisible(observerState) && (
        <div
          className="hospitation-indicator-fixed"
          title="Hospitation zur Qualitätssicherung"
          aria-label="Ein Administrator hospitiert diese Session"
          style={{ position: 'fixed', top: 10, right: 12, zIndex: 9999, fontSize: '1.4rem', cursor: 'help', background: 'rgba(0,0,0,0.55)', borderRadius: '50%', padding: '4px 6px', lineHeight: 1 }}
        >👁️</div>
      )}
      {clientRole === 'observer' && !observerAudioUnlocked && (
        <button
          type="button"
          onClick={unlockObserverAudio}
          title="Ton der Hospitation aktivieren"
          style={{ position: 'fixed', top: 10, left: '50%', transform: 'translateX(-50%)', zIndex: 10000, background: '#e74c3c', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 16px', fontSize: '1rem', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.4)' }}
        >🔊 Ton aktivieren</button>
      )}
    </>
  );

  // === NEW: View-based Rendering ===
  // Handle view changes instead of legacy isQuizStarted logic
  if (currentView === 'waiting') {
    // Wenn modStreamAvailable true ist, setze checkStates.teacher auf true (falls noch nicht gesetzt)
    if (modStreamAvailable && !checkStates.teacher) {
      setCheckStates(prev => ({ ...prev, teacher: true }));
    }
    const firstName = authUser?.name ? authUser.name.split(' ')[0] : null;
    return (
      <>
        {hospitationIndicator}
        <div className="waitingRoom">
          <audio id="modAudio" autoPlay style={{ display: "none" }} />
          <div className="ksBrandBadge">
            <span className="ksBrandDot"><img src="/images/klugeschule-logo.png" alt="" /></span>
            Kluge&nbsp;Schule
          </div>
          <div className="waitingRoomGrid">

            {/* LINKS: Begrüßung + Videos */}
            <div className="waitingRoomLeft">
              <h2 className="waitingRoomHeading">
                {firstName ? `Hallo, ${firstName}!` : 'Willkommen!'}
              </h2>
              <p className="waitingRoomSubtext">
                Gleich geht es los – wir bereiten alles für dich vor.
              </p>
              <div className="waitingRoomVideos">
                <div className="waitingRoomVideoWrapper">
                  <video
                    ref={studentSelfVideoRef}
                    className="waitingRoomVideo"
                    autoPlay
                    muted
                    playsInline
                  />
                  <span className="waitingRoomVideoLabel">Du</span>
                </div>
                <div className="waitingRoomVideoWrapper">
                  <video
                    ref={modPreviewRef}
                    id="modPreview"
                    className="modPreview"
                    autoPlay
                    playsInline
                    muted
                  />
                  <span className="waitingRoomVideoLabel">{modDisplayName || 'Lehrer'}</span>
                </div>
              </div>
            </div>

            {/* RECHTS: Checkliste */}
            <div className="waitingRoomRight">
              <ul className="checklist">
                <li className={`checkItem ${checkStates.connected ? "checked" : ""}`}>
                  <span className="spinner" /> Verbindung aufgebaut
                </li>
                <li className={`checkItem ${checkStates.system ? "checked" : ""}`}>
                  <span className="spinner" /> Systemcheck
                </li>
                <li className={`checkItem ${checkStates.teacher ? "checked" : ""}`}>
                  <span className="spinner" /> Warten auf Lehrer
                </li>
              </ul>
            </div>

          </div>
        </div>
      </>
    );
  }

  // === NEW: Video View ===
  if (currentView === 'whiteboard') {
    return (
      <>
        {hospitationIndicator}
        <div className="wb-stage student">
          <Whiteboard socketRef={socket} roomId={roomId} readOnly={true} />
          <div className="wb-video-overlay">
            <video ref={modPreviewRef} autoPlay playsInline muted />
          </div>
          <audio id="modAudio" autoPlay style={{ display: 'none' }} />
        </div>
      </>
    );
  }

  if (currentView === 'video') {
    return (
      <>
        {hospitationIndicator}
        <VideoView
          modVideoRef={modPreviewRef}
          studentSelfVideoRef={studentSelfVideoRef}
          modStreamAvailable={modStreamAvailable}
          modVideoMuted={modVideoMuted}
          audioJoined={audioJoined}
        />
      </>
    );
  }

  if (loading) {
    return <>{hospitationIndicator}<div className="loadingScreen">Loading session...</div></>;
  }

  // Schutzklausel: Quiz-View ohne Fragen → Wartemeldung
  // WICHTIG: Für Video-Sessions (ohne Fragen) trotzdem Quiz-View erlauben
  if (currentView === 'quiz' && !isVideoSession && (!questions.length || !currentQuestion)) {
    return (
      <>
        {hospitationIndicator}
        <div className="loadingScreen">
          <p>Der Dozent hat noch keine Fragen für diese Session zugewiesen.</p>
          <p style={{ fontSize: '0.85em', opacity: 0.7 }}>Fragen werden automatisch geladen, sobald der Dozent sie hinzufügt.</p>
        </div>
      </>
    );
  }

  // === Success/Pokal View ===
  if (currentView === 'success') {
    const firstName = authUser?.name ? authUser.name.split(' ')[0] : null;
    const totalQ = questions.length;
    const answeredCount = answeredQuestions.size;
    const wrongCount = Math.max(0, answeredCount - correctAnswers);
    const unansweredCount = Math.max(0, totalQ - answeredCount);
    // Prozent bezieht sich nur auf beantwortete Fragen – Unbeantwortete drücken die Quote nicht.
    const pct = answeredCount > 0 ? Math.round((correctAnswers / answeredCount) * 100) : 0;
    return (
      <>
        {hospitationIndicator}
        <div className="successPageWrapper">
          <div className="successConfettiLayer"></div>
          <div className="successContent">
            <div className="trophyBounce">🏆</div>
            <h1 className="successHeadline">
              {firstName ? `${firstName}, super gemacht!` : 'Super gemacht!'}
            </h1>
            {totalQ > 0 && (
              <div className="successScoreCard">
                <div className="successScoreCircle">
                  <span className="successScorePct">{pct}%</span>
                  <span className="successScoreLabel">Richtig</span>
                </div>
                <div className="successScoreDetails">
                  <div className="successScoreRow correct">
                    <span className="successScoreIcon">✓</span>
                    <span>{correctAnswers} richtig</span>
                  </div>
                  <div className="successScoreRow wrong">
                    <span className="successScoreIcon">✗</span>
                    <span>{wrongCount} falsch</span>
                  </div>
                  {unansweredCount > 0 && (
                    <div className="successScoreRow unanswered">
                      <span className="successScoreIcon">–</span>
                      <span>{unansweredCount} nicht beantwortet</span>
                    </div>
                  )}
                  <div className="successScoreRow total">
                    <span className="successScoreIcon">∑</span>
                    <span>{totalQ} Fragen gesamt</span>
                  </div>
                </div>
              </div>
            )}
            {!totalQ && (
              <p className="successSubtitle">Du hast heute toll mitgemacht! 🌟</p>
            )}
            <div className="successStars">⭐ ⭐ ⭐</div>
          </div>
        </div>
      </>
    );
  }

  // === NEW: Only render Quiz if currentView === 'quiz' ===
  if (currentView !== 'quiz') {
    return <>{hospitationIndicator}<div className="loadingScreen">Waiting for moderator...</div></>;
  }

  const handleAnswer = (answer) => {
    if (selectedAnswer !== null) return;
    console.log("🎯 DEBUG handleAnswer:");
    console.log("  selectedAnswer:", answer);
    console.log("  currentQuestion.correct:", currentQuestion.correct);
    console.log("  comparison result:", answer === currentQuestion.correct);
    setSelectedAnswer(answer);
    if (answer === currentQuestion.correct) {
      setCorrectAnswers(prev => prev + 1);
      console.log("  ✅ CORRECT ANSWER!");
    } else {
      console.log("  ❌ WRONG ANSWER!");
    }
    setTimeout(() => setShowNextButton(true), 800);
  };

  const handleDrop = (isCorrect, draggedId = null) => {
    if (!isCorrect) {
      setSelectedAnswer("wrong");
      setTimeout(() => setShowNextButton(true), 800);
      return;
    }

    if (currentQuestion.type === "multiDragdrop") {
      if (draggedId) {
        const updatedPlaced = [...placedItems, draggedId];
        setPlacedItems(updatedPlaced);
        if (updatedPlaced.length === currentQuestion.pieces.length) {
          setSelectedAnswer("correct");
          setCorrectAnswers(prev => prev + 1);
          setTimeout(() => setShowNextButton(true), 800);
        }
      }
    } else {
      setSelectedAnswer("correct");
      setCorrectAnswers(prev => prev + 1);
      setTimeout(() => setShowNextButton(true), 800);
    }
  };

  // ---- Helfer für neue Fragentypen ----
  const nzParse = (v, fb) => {
    if (v === null || v === undefined) return fb;
    if (typeof v === "string") { try { return JSON.parse(v); } catch (e) { return fb; } }
    return v;
  };
  const nzNorm = (x) => String(x === null || x === undefined ? "" : x).trim().toLowerCase().replace(/\s+/g, " ");
  const finishComplex = (isCorrect) => {
    if (selectedAnswer !== null) return;
    setSelectedAnswer(isCorrect ? "correct" : "wrong");
    if (isCorrect) setCorrectAnswers((prev) => prev + 1);
    setTimeout(() => setShowNextButton(true), 800);
  };

  const renderOptions = () => {
    if (!currentQuestion) return null;
    switch (currentQuestion.type) {
      case "multipleChoice":
      case "imageQuestion":
        return (
          <div className="optionsGridStyle">
            {Object.entries(currentQuestion.options).map(([key, value]) => {
              const isSelected = selectedAnswer && key === selectedAnswer;
              const isCorrect = key === currentQuestion.correct;
              const cssClass = `buttonStyle ${isSelected ? (isCorrect ? "button-correct" : "button-wrong") : ""}`;
              
              if (selectedAnswer) {
                console.log(`🔍 Button ${key}: selected=${isSelected}, correct=${isCorrect}, class=${cssClass}`);
              }
              
              return (
                <button
                  key={key}
                  onClick={() => handleAnswer(key)}
                  className={cssClass}
                  disabled={selectedAnswer !== null}
                >
                  {key}) {value}
                </button>
              );
            })}
          </div>
        );
      case "yesno":
        return (
          <div className="yesnoContainer">
            {["Ja", "Nein"].map((option) => (
              <button
                key={option}
                onClick={() => handleAnswer(option)}
                className={`buttonStyle yesnoButton ${option === "Ja" ? "yes-button" : "no-button"} ${selectedAnswer && option === selectedAnswer ? (
                  option === currentQuestion.correct ? "button-correct" : "button-wrong"
                ) : ""}`}
                disabled={selectedAnswer !== null}
              >
                {/* Text wird durch CSS :before Symbole ersetzt */}
              </button>
            ))}
          </div>
        );
      case "dragdrop":
        return (
          <DndContext
            onDragEnd={({ active, over }) => {
              if (selectedAnswer !== null) return;
              if (over) {
                const droppedTarget = currentQuestion.dropTargets.find(target => target.id === over.id);
                if (droppedTarget) {
                  handleDrop(droppedTarget.correct);
                }
              }
            }}
          >
            <div className="dragDropContainer">
              {selectedAnswer === null && (
                <DraggableItem id="dragItem">{currentQuestion.dragItem}</DraggableItem>
              )}
              <div className="dropTargets">
                {currentQuestion.dropTargets.map((target) => (
                  <DroppableZone key={target.id} id={target.id}>
                    {target.label ? target.label : (
                      <img src={target.image} alt="Option" className="dropImage" />
                    )}
                  </DroppableZone>
                ))}
              </div>
            </div>
          </DndContext>
        );
      case "multiDragdrop": {
        const answered = selectedAnswer !== null;
        const placedPieceIds = Object.values(multiPlacements).map(String);
        const poolPieces = currentQuestion.pieces.filter(
          (p) => !placedPieceIds.includes(String(p.id))
        );
        return (
          <DndContext
            onDragEnd={({ active, over }) => {
              if (selectedAnswer !== null) return;
              if (!over) return;
              const pieceId = active.id;
              const targetId = over.id;
              setMultiPlacements((prev) => {
                const next = {};
                Object.entries(prev).forEach(([tId, pId]) => {
                  if (String(pId) === String(pieceId)) return; // Teil war woanders -> loesen
                  if (String(tId) === String(targetId)) return; // Ziel neu belegt -> altes Teil zurueck in Pool
                  next[tId] = pId;
                });
                next[targetId] = pieceId;
                return next;
              });
            }}
          >
            <div className="multiDragDropContainer">
              <div className="dropZoneContainer">
                {currentQuestion.targets.map((target) => {
                  const placedPieceId = multiPlacements[target.id];
                  const placedPiece = currentQuestion.pieces.find(
                    (p) => String(p.id) === String(placedPieceId)
                  );
                  const isCorrectPlacement =
                    placedPieceId != null && String(placedPieceId) === String(target.id);
                  return (
                    <DroppableZone key={target.id} id={target.id}>
                      <div className="multiTargetLabel">
                        {target.image
                          ? <img src={target.image} alt="Ziel" className="dragImage" />
                          : (target.label || target.text)}
                      </div>
                      {placedPiece && (
                        <DraggableItem key={placedPiece.id} id={placedPiece.id} disabled={answered}>
                          <div className={`multiPlacedPiece ${answered ? (isCorrectPlacement ? "button-correct" : "button-wrong") : ""}`}>
                            {placedPiece.image
                              ? <img src={placedPiece.image} alt="Drag" className="dragImage" />
                              : <span className="dragLabel">{placedPiece.label || placedPiece.text || placedPiece.id}</span>}
                          </div>
                        </DraggableItem>
                      )}
                    </DroppableZone>
                  );
                })}
              </div>
              <div className="dragItemsContainer">
                {poolPieces.map((piece) => (
                  <DraggableItem key={piece.id} id={piece.id} disabled={answered}>
                    {piece.image
                      ? <img src={piece.image} alt="Drag" className="dragImage" />
                      : <span className="dragLabel">{piece.label || piece.text || piece.id}</span>}
                  </DraggableItem>
                ))}
              </div>
            </div>
          </DndContext>
        );
      }
      case "lueckentext_ausfuellen": {
        const gaps = nzParse(currentQuestion.gaps, []);
        const parts = String(currentQuestion.question || "").split(/\{\{(\d+)\}\}/);
        const answered = selectedAnswer !== null;
        return (
          <div className="lueckentextContainer">
            <p className="lueckentextSatz">
              {parts.map((part, i) => {
                if (i % 2 === 1) {
                  const gi = parseInt(part, 10) - 1;
                  const alts = Array.isArray(gaps[gi]) ? gaps[gi] : (gaps[gi] != null ? [gaps[gi]] : []);
                  const ok = answered && alts.some((a) => nzNorm(a) === nzNorm(gapInputs[gi]));
                  return (
                    <input key={i} type="text" className={`lueckeInput ${answered ? (ok ? "button-correct" : "button-wrong") : ""}`}
                      value={gapInputs[gi] || ""} disabled={answered}
                      onChange={(e) => setGapInputs((prev) => ({ ...prev, [gi]: e.target.value }))} />
                  );
                }
                return <span key={i}>{part}</span>;
              })}
            </p>
            {!answered && (
              <button className="buttonStyle pruefenButton" onClick={() => {
                let allOk = gaps.length > 0;
                gaps.forEach((alts, gi) => {
                  const a = Array.isArray(alts) ? alts : [alts];
                  if (!a.some((x) => nzNorm(x) === nzNorm(gapInputs[gi]))) allOk = false;
                });
                finishComplex(allOk);
              }}>Prüfen</button>
            )}
          </div>
        );
      }
      case "lueckentext_zuordnung": {
        const gaps = nzParse(currentQuestion.gaps, []);
        const bank = nzParse(currentQuestion.words, []);
        const parts = String(currentQuestion.question || "").split(/\{\{(\d+)\}\}/);
        const answered = selectedAnswer !== null;
        const matchOk = (gi) => {
          const sol = gaps[gi];
          const sols = Array.isArray(sol) ? sol : [sol];
          return sols.some((x) => nzNorm(x) === nzNorm(gapInputs[gi]));
        };
        return (
          <div className="lueckentextContainer">
            <p className="lueckentextSatz">
              {parts.map((part, i) => {
                if (i % 2 === 1) {
                  const gi = parseInt(part, 10) - 1;
                  const ok = answered && matchOk(gi);
                  return (
                    <select key={i} className={`lueckeSelect ${answered ? (ok ? "button-correct" : "button-wrong") : ""}`}
                      value={gapInputs[gi] || ""} disabled={answered}
                      onChange={(e) => setGapInputs((prev) => ({ ...prev, [gi]: e.target.value }))}>
                      <option value="">– wählen –</option>
                      {bank.map((w, k) => <option key={k} value={w}>{w}</option>)}
                    </select>
                  );
                }
                return <span key={i}>{part}</span>;
              })}
            </p>
            {!answered && (
              <button className="buttonStyle pruefenButton" onClick={() => {
                let allOk = gaps.length > 0;
                gaps.forEach((_g, gi) => { if (!matchOk(gi)) allOk = false; });
                finishComplex(allOk);
              }}>Prüfen</button>
            )}
          </div>
        );
      }
      case "satzpuzzle": {
        const order = nzParse(currentQuestion.words, []);
        const shuffled = shuffleArray(order, mulberry32((currentQuestion.id || 1) >>> 0));
        const answered = selectedAnswer !== null;
        const remaining = shuffled.map((w, i) => i).filter((i) => !puzzleOrder.includes(i));
        const built = puzzleOrder.map((i) => shuffled[i]);
        return (
          <div className="satzpuzzleContainer">
            <div className="satzpuzzleAnswerRow">
              {built.length === 0
                ? <span className="satzpuzzlePlaceholder">Tippe die Wörter der Reihe nach an…</span>
                : built.map((w, k) => (
                  <span key={k} className="satzpuzzleTile chosen" onClick={() => { if (!answered) setPuzzleOrder((prev) => prev.filter((_v, idx) => idx !== k)); }}>{w}</span>
                ))}
            </div>
            <div className="satzpuzzleBank">
              {remaining.map((i) => (
                <span key={i} className="satzpuzzleTile" onClick={() => { if (!answered) setPuzzleOrder((prev) => [...prev, i]); }}>{shuffled[i]}</span>
              ))}
            </div>
            {!answered && puzzleOrder.length === order.length && (
              <button className="buttonStyle pruefenButton" onClick={() => {
                finishComplex(built.length === order.length && built.every((w, k) => w === order[k]));
              }}>Prüfen</button>
            )}
          </div>
        );
      }
      case "wortmarkierung": {
        const words = String(currentQuestion.question || "").split(/\s+/).filter(Boolean);
        const ci = parseInt(currentQuestion.correct, 10);
        const answered = selectedAnswer !== null;
        return (
          <div className="wortmarkierungContainer">
            {words.map((w, i) => {
              let cls = "wortToken";
              if (answered) {
                if (i === ci) cls += " button-correct";
                else if (String(i) === String(selectedAnswer)) cls += " button-wrong";
              }
              return <span key={i} className={cls} onClick={() => { if (!answered) handleAnswer(String(i)); }}>{w}</span>;
            })}
          </div>
        );
      }
      case "multi_multiplechoice": {
        const opts = nzParse(currentQuestion.options, {});
        const correctKeys = nzParse(currentQuestion.words, []).map(String);
        const answered = selectedAnswer !== null;
        return (
          <div className="optionsGridStyle">
            {Object.entries(opts).map(([key, value]) => {
              const sel = mcSelected.includes(key);
              const isC = correctKeys.includes(String(key));
              let cls = "buttonStyle multiMcOption" + (sel ? " selected" : "");
              if (answered && isC) cls += " button-correct";
              if (answered && sel && !isC) cls += " button-wrong";
              return (
                <button key={key} className={cls} disabled={answered}
                  onClick={() => setMcSelected((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key])}>
                  {sel ? "☑" : "☐"} {key}) {value}
                </button>
              );
            })}
            {!answered && (
              <button className="buttonStyle pruefenButton" onClick={() => {
                const a = [...mcSelected].map(String).sort();
                const b = [...correctKeys].map(String).sort();
                finishComplex(a.length === b.length && a.every((k, i) => k === b[i]));
              }}>Prüfen</button>
            )}
          </div>
        );
      }
      case "kreuzwortraetsel": {
        const entries = nzParse(currentQuestion.grid, []);
        const cells = {}; let maxR = 0, maxC = 0;
        entries.forEach((e) => {
          const ans = String(e.answer || "");
          for (let k = 0; k < ans.length; k++) {
            const r = e.dir === "down" ? e.row + k : e.row;
            const c = e.dir === "down" ? e.col : e.col + k;
            cells[`${r},${c}`] = ans[k];
            maxR = Math.max(maxR, r); maxC = Math.max(maxC, c);
          }
        });
        const answered = selectedAnswer !== null;
        const rows = [];
        for (let r = 0; r <= maxR; r++) {
          const tds = [];
          for (let c = 0; c <= maxC; c++) {
            const kk = `${r},${c}`;
            if (cells[kk]) {
              const ok = answered && nzNorm(cwInputs[kk]) === nzNorm(cells[kk]);
              tds.push(
                <td key={c} className="cwCell">
                  <input maxLength={1} className={`cwInput ${answered ? (ok ? "button-correct" : "button-wrong") : ""}`}
                    value={cwInputs[kk] || ""} disabled={answered}
                    onChange={(e) => setCwInputs((prev) => ({ ...prev, [kk]: e.target.value.slice(-1) }))} />
                </td>
              );
            } else {
              tds.push(<td key={c} className="cwCellEmpty"></td>);
            }
          }
          rows.push(<tr key={r}>{tds}</tr>);
        }
        return (
          <div className="kreuzwortraetselContainer">
            <table className="cwTable"><tbody>{rows}</tbody></table>
            <div className="cwClues">
              {entries.map((e, i) => (
                <div key={i} className="cwClue">{e.dir === "down" ? "↓" : "→"} {e.clue}</div>
              ))}
            </div>
            {!answered && (
              <button className="buttonStyle pruefenButton" onClick={() => {
                let allOk = Object.keys(cells).length > 0;
                Object.entries(cells).forEach(([kk, sol]) => { if (nzNorm(cwInputs[kk]) !== nzNorm(sol)) allOk = false; });
                finishComplex(allOk);
              }}>Prüfen</button>
            )}
          </div>
        );
      }
      default:
        return <p>Unbekannter Fragetyp</p>;
    }
  };

  // ✅ Hier die korrekte Antwortprüfung OHNE extra Funktion
  const SENTINEL_TYPES = ["dragdrop", "multiDragdrop", "lueckentext_ausfuellen", "lueckentext_zuordnung", "satzpuzzle", "multi_multiplechoice", "kreuzwortraetsel"];
  const answerCorrect = currentQuestion && (selectedAnswer !== null && (
    SENTINEL_TYPES.includes(currentQuestion.type)
      ? selectedAnswer === "correct"
      : selectedAnswer === currentQuestion.correct
  ));


  // === Schüler-Design nach Klassenstufe ===
  // Klasse 1–3  -> "kids" (kindgerecht, ruhig).  Alle anderen / unbekannt -> "cockpit".
  // Test-Override per URL: ?theme=kids | ?theme=cockpit
  const themeOverride = (() => {
    try {
      const t = new URLSearchParams(window.location.search).get('theme');
      return (t === 'kids' || t === 'cockpit') ? t : null;
    } catch (e) { return null; }
  })();
  const gradeNum = parseInt((String(klassenstufe).match(/\d+/) || [])[0], 10);
  const studentTheme = themeOverride
    || ((gradeNum >= 1 && gradeNum <= 3) ? 'kids' : 'cockpit');

  // === QUIZ RENDERING ===
  return (
    <div className={`lrStudentRoot lrTheme-${studentTheme}`}>
      {hospitationIndicator}
      <audio id="modAudio" autoPlay style={{ display: "none" }} />
      <div className="fixedBackgroundStyle"></div>

      {/* Themen-Chrome: dekorative Rahmen-Elemente je nach Design */}
      {studentTheme === 'cockpit' && (
        <div className="lrRailChrome" aria-hidden="true">
          <div className="lrBrand"><span className="lrBrandDot"><img src="/images/klugeschule-logo.png" alt="" /></span> Kluge&nbsp;Schule</div>
          <div className="lrPresence"><span className="lrPresenceDot" /> Dein Lehrer ist im Raum</div>
          <div className="lrRailMeLabel">Deine Kamera</div>
        </div>
      )}

      {/* Video-Elemente immer anzeigen (sowohl für Video-Sessions als auch Quiz-Sessions) */}
      <div className="videoContainerStyle">
        <video
          ref={modPreviewRef}
          className="videoPlayer"
          autoPlay
          playsInline
          muted
          onLoadedMetadata={() => {
            console.log("📹 Dozenten-Video Metadaten geladen");
          }}
          onError={(e) => {
            console.error("❌ Dozenten-Video Fehler:", e);
          }}
        />
        <div className="lrTeacherLabel">{modDisplayName || 'Lehrer'}</div>
      </div>

      {/* Schüler-Kamera unten links */}
      <div className="studentVideoContainer">
        <video
          ref={studentSelfVideoRef}
          className="studentVideoQuiz"
          autoPlay
          muted
          playsInline
        />
        <div className="studentVideoLabel">Du</div>
      </div>

      <div className="pageStyle">
        {/* Fallback für Video-Sessions ohne Fragen */}
        {!currentQuestion && isVideoSession ? (
          <div className="noQuizContent">
            <h1>📺 Video-Session</h1>
            <p>Diese Session hat keine Quiz-Fragen zugewiesen.</p>
            <p>Der Dozent kann über den View-Slider zwischen verschiedenen Modi wechseln.</p>
          </div>
        ) : currentQuestion ? (
          <>
            {/* Fortschrittsbalken */}
            <div className="progressContainer">
              <div className="progressBar">
                <div className="progressFill" style={{ width: `${progressPercent}%` }} />
              </div>
              <p className="progressText">
                Frage {currentQuestionIndex + 1} von {questions.length}
              </p>
            </div>

            {/* Quizbox */}
            <div
              className={
                "quizBoxStyle"
                + (currentQuestion.type === "imageQuestion" ? " imageQuestion" : "")
                + (currentQuestion.type === "dragdrop" ? " dragdrop" : "")
                + (currentQuestion.type === "multiDragdrop" ? " multidragdrop" : "")
              }
              ref={quizBoxRef}
            >
              {selectedAnswer && !quizFinished && (
                <div className="feedbackBannerWrapper">
                  <div className={`feedbackBanner ${answerCorrect ? "feedbackCorrect" : "feedbackWrong"}`}>
                    {answerCorrect
                      ? "🎉 Richtig!"
                      : "❌ Falsch!"}
                  </div>
                  {showNextButton && canStudentNavigate && (
                    <button
                      onClick={handleNext}
                      className="nextFixedButton feedbackBannerButton"
                      aria-label="weiter"
                    >
                      weiter&nbsp;
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="32"
                        height="32"
                        fill="#fff"
                        viewBox="0 0 16 16"
                      >
                        <path
                          fillRule="evenodd"
                          d="M1 8a.5.5 0 0 1 .5-.5h11.793l-3.147-3.146a.5.5 0 0 1 .708-.708l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708-.708L13.293 8.5H1.5A.5.5 0 0 1 1 8z"
                        />
                      </svg>
                    </button>
                  )}
                  {showNextButton && !canStudentNavigate && (
                    <button
                      disabled
                      className="nextFixedButton feedbackBannerButton disabled"
                      aria-label="warten"
                      style={{ opacity: 0.5, cursor: 'not-allowed', backgroundColor: '#888' }}
                    >
                      warten... 
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="32"
                        height="32"
                        fill="#fff"
                        viewBox="0 0 16 16"
                      >
                        <path
                          fillRule="evenodd"
                          d="M1 8a.5.5 0 0 1 .5-.5h11.793l-3.147-3.146a.5.5 0 0 1 .708-.708l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708-.708L13.293 8.5H1.5A.5.5 0 0 1 1 8z"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              )}
              
              <div className="questionContainerStyle">
                {currentQuestion.type === "imageQuestion" && currentQuestion.image && (
                  <img
                    src={currentQuestion.image}
                    alt="Fragebild"
                    className="questionImage"
                  />
                )}
                <div className="questionContent">
                  <h1>
                    Frage {currentQuestionIndex + 1}
                  </h1>
                  {!["wortmarkierung", "lueckentext_ausfuellen", "lueckentext_zuordnung"].includes(currentQuestion.type) && (
                    <p>
                      {currentQuestion.question}
                    </p>
                  )}
                  {renderOptions()}
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="loadingScreen">Loading quiz...</div>
        )}
      </div>

      {calculatorMode === 'basic' && (
        <CalculatorWindow title="Rechner" allowClose={false} onClose={() => {}}>
          <BasicCalculator />
        </CalculatorWindow>
      )}
      {calculatorMode === 'full' && (
        <CalculatorWindow title="Rechner" allowClose={false} onClose={() => {}}>
          <GeoGebraCalculator />
        </CalculatorWindow>
      )}
    </div>
  );
  }
}

// --- DND-Kit Hilfskomponenten für Drag & Drop ---
function DraggableItem({ id, children, disabled }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id, disabled });
  const style = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    opacity: disabled ? 0.5 : isDragging ? 0.8 : 1,
    cursor: disabled ? "not-allowed" : "grab",
    touchAction: "none"
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={`draggableDndKit${disabled ? " dragItemPlaced" : ""}`}
    >
      {children}
    </div>
  );
}
function DroppableZone({ id, children }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      style={{
        border: isOver ? "2px solid #00cec9" : "2px dashed #00cec9",
        borderRadius: 12,
        minHeight: 120,
        minWidth: 120,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: isOver ? "#b2f7ef" : "#dfe6e9",
        margin: 12,
      }}
    >
      {children}
    </div>
  );
}

export default function AppRouter() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/room/:roomId" element={<LearningRoom />} />
        <Route path="/mod/:roomId" element={<ModView />} />
        <Route path="/" element={<div>Bitte eine gültige Raum-ID aufrufen.</div>} />
      </Routes>
    </BrowserRouter>
  );
}/* Cache-Bust WebSocket-Clean-Version-20250826-1635 */
